import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { 
  InscripcionOs, 
  Familiar, 
  Parentesco, 
  Nacionalidad, 
  Sexo,
  RegimenLaboral,
  TipoDocumento,
  EstadoCivil
} from "./interfaz-inscripcion-os/inscripcion-os-interface";
import { DeclaracionJurada, GrupoSanguineo } from "./interfaz-ddjj-os/ddjj-os-interface";
import { debounceTime } from "rxjs/operators";
import { Subscription } from "rxjs";
import { AuthService } from "@services/auth/auth.service";
import { MediaService } from "@services/shared/media.service";
import { GuiMsjService } from "@services/shared/gui-msj.service";
import { UsuarioService } from "@services/shared/usuario.service";
import { StorageService } from "@services/shared/storage.service";
import { InscripcionTraceService } from "@services/shared/inscripcion-trace.service";
import { AuditoriaAccion } from "@enum/auditoria/auditoria-keys.enum";
import { Router, ActivatedRoute } from "@angular/router";
import { APP_CONFIG_OFERTAS, IAppOfertas } from "src/app/app.config/app.config-ofertas";
import { SolicitudesService } from "@services/ofertas-laborales/solicitudes.service";
import { DocumentosService } from "@services/documentos/documentos.service";
import { ActividadesService } from "@services/ofertas-laborales/actividades.service";
import { environment } from '@env/environment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

interface FileSlot {
  label: string;
  descripcion: string;
  file: File | null;
  preview: { url: string | ArrayBuffer; name: string; type: string; isImage: boolean; safeUrl?: SafeUrl } | null;
  idDoc?: string;
  error?: string;
  uploaded?: boolean;
}

interface SubidaFallida {
  tipo: 'dni' | 'actividad' | 'familiares';
  slotIndex: number;
  label: string;
  archivo: string;
}

@Component({
  selector: 'app-inscripcion-os',
  templateUrl: './inscripcion-os.component.html',
  styleUrls: ['./inscripcion-os.component.scss'],
})
export class InscripcionOsComponent implements OnInit, OnDestroy {

  Parentesco = Parentesco;
  Nacionalidad = Nacionalidad;
  Sexo = Sexo;
  RegimenLaboral = RegimenLaboral;
  TipoDocumento = TipoDocumento;
  EstadoCivil = EstadoCivil;

  inscripcionForm: FormGroup;

  flagRegistrando = false;

  // Patrones de validación actualizados
  patronAlfabetico = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ'\s]{0,50}$/;
  patronAlfabeticoConNumeros = /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ'\s.,-]{0,100}$/;
  patronNumerico = /^\d{0,10}$/; // DNI: solo números, máximo 10
  patronCuil = /^\d{0,12}$/; // CUIL/CUIT: solo números, máximo 11
  patronTelefono = /^\d{0,15}$/; // Teléfono: solo números, máximo 15
  patronEmail = /^[^@]+@[^@]+\.[^@]+$/; // Email simple con @
  patronNumeroDireccion = /^[0-9A-Za-z]{0,10}$/; // Número dirección: letras y números, máximo 10
  patronPiso = /^[0-9A-Za-z\-]{0,5}$/; // Piso: números, letras, guión, máximo 5
  patronDepartamento = /^[0-9A-Za-z]{0,5}$/; // Departamento: números y letras, máximo 5
  patronCodigoPostal = /^[0-9A-Za-z]{0,8}$/; // Código postal: números y letras, máximo 8
  patronRazonSocial = /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ\s.,&-]{0,100}$/; // Razón social

  modoAdmin = environment.modoAdmin;

  selectedDniFiles: File[] = [];
  dniFilePreviews: { url: string | ArrayBuffer, name: string, type: string, isImage: boolean }[] = [];
  isUploadingFile: boolean = false;

  // Barra de progreso de la subida (variant "bar" del app-spinner)
  progresoSubida: number | null = null;
  textoProgreso = 'Procesando su inscripción, por favor espere';
  avisoSubida = '';
  private uploadTotal = 0;
  private uploadCompletos = 0;
  private uploadArchivoPct = 0;
  private avanceSub: Subscription;
  private avisoSubidaTimer: any;

  fileUploadError: string = '';
  showFileSizeError = false;
  showConfirmarAfiliacionModal = false;
  showDeviceWarningModal = window.innerWidth <= 768;
  showArchivoDuplicadoModal = false;
  mensajeArchivoDuplicado = '';
  showRegistroErrorModal = false;

  // Transición inscripción -> DDJJ (sin ventana viva ni modal de decisión)
  avisoPaso1Completado = false;
  // Errores visibles de DDJJ (nunca silenciosos)
  showDdjjErrorModal = false;
  ddjjErrorMensaje = '';

  // Subida resiliente: si un archivo falla, se reintenta/cambia sobre la misma solicitud.
  showSubidaFallidaModal = false;
  subidasFallidas: SubidaFallida[] = [];
  solicitudPendienteId: string | null = null;
  // Gancho DEV (solo cuando environment.modoAdmin): fuerza un falso error de subida.
  simularErrorSubidaAdmin = false;

  mensajeDispositivoWarning = 'Si su celular es antiguo, puede tener problemas para sacar fotos desde esta página. A veces el navegador se cierra solo porque el teléfono <b>no tiene suficiente memoria</b>.\n\n'
    + 'Para que <b>no pierda lo que ya cargó</b>, le recomendamos:\n\n'
    + '<b>1.</b> Saque la foto con la cámara de su teléfono <b>antes de empezar</b>.\n'
    + '<b>2.</b> Cuando le pidamos un documento, elija la opción <b>"Seleccionar archivo"</b> y busque la foto que ya sacó.\n\n'
    + 'Así evita cualquier inconveniente y puede completar su inscripción sin problemas.';

  selectedImageForModal: string | null = null;

  showFamiliarModal: boolean = false;
  editingFamiliarIndex: number | null = null;
  familiarForm: FormGroup;

  actividadSeleccionada: any = null;
  actividadDocumentosRequeridos: any[] = [];
  selectedActividadFiles: File[] = [];
  actividadFilePreviews: { url: string | ArrayBuffer, name: string, type: string, isImage: boolean }[] = [];
  actividadFileUploadError: string = '';

  selectedFamiliaresFiles: File[] = [];
  familiaresFilePreviews: { url: string | ArrayBuffer, name: string, type: string, isImage: boolean }[] = [];
  familiaresFileUploadError: string = '';

  //
  showEmpresaSection: boolean = true;

  currentStep = 1;
  maxStepReached = 1;
  steps: string[] = ['Personales', 'Contacto', 'Laboral', 'Familiares', 'DNI/CODEM', 'Docs Act', 'Docs Fam', 'Revisión'];

  dniFileSlots: FileSlot[] = [];
  actividadFileSlots: FileSlot[] = [];
  familiaresFileSlots: FileSlot[] = [];

  stepErrors: boolean[] = [false, false, false, false, false, false, false, false];

  phase: 'inscripcion' | 'ddjj' = 'inscripcion';
  solicitudId: string;
  inscripcionData: any;
  esMonotributo = false;
  declaracionForm!: FormGroup;
  gruposSanguineos = Object.values(GrupoSanguineo);

  abrirNav = false;
  esMovil = false;

  showConfirmarDdjjModal = false;
  showDdjjExitosaModal = false;

  estadoSecciones: { [key: string]: boolean } = {
    'datosPersonales': false,
    'antecedentesMedicos': false, 
    'habitosFisiologicos': false,
    'habitosToxicos': false,
    'antecedentesGinecoObstetricos': false,
    'antecedentesFamiliares': false,
    'antecedentesPatologicos': false,
    'sintomasYExperiencias': false,
    'antecedentesQuirurgicos': false
  };

  progresoTotal = 0;

  patronAlfabeticoDdjj = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ'\s-]+$/;
  patronAlfanumericoDdjj = /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ'&@\s.,;:()-]+$/;
  patronNumericoDdjj = /^[0-9-]+$/;

  isUploadingDDJJ = false;

  ddjjStep = 1;
  ddjjMaxStep = 1;
  ddjjSteps: string[] = ['Personales', 'Médicos', 'Fisiológicos', 'Tóxicos', 'Gineco-Obst.', 'Familiares', 'Patológicos', 'Síntomas', 'Quirúrgicos', 'Revisión'];
  ddjjStepErrors: boolean[] = Array(10).fill(false);

  constructor(  private fb: FormBuilder,
                private authService: AuthService,
                private guiMsjService: GuiMsjService,
                private usuarioService: UsuarioService,
                private storageService: StorageService,
                private inscripcionTrace: InscripcionTraceService,
                private mediaService: MediaService,
                private router: Router,
                private solicitudesService: SolicitudesService,
                @Inject(APP_CONFIG_OFERTAS)
                public iAppOfertas: IAppOfertas,
                private documentosService: DocumentosService,
                private actividadesService: ActividadesService,
                private sanitizer: DomSanitizer,
                private route: ActivatedRoute ) {  }

  ngOnInit() {
    this.inscripcionTrace.startTrace();
    this.avanceSub = this.mediaService.getAvance().subscribe((resp) => this.onAvanceSubida(resp));
    this.createForm();
    this.createFormDdjj();
    this.initFamiliarForm();
    this.buildDniFileSlots();
    this.setupAllConditionalValidations();

    this.inscripcionForm.get('datosDelTitular.regimenLaboral')?.valueChanges.subscribe(regimen => {
      if (regimen != null) {
        this.cargarActividadPorRegimen(regimen);

        const isMonotributo = regimen === RegimenLaboral.Monotributo;
        this.showEmpresaSection = !isMonotributo;

        if (isMonotributo) {
          const empresaGroup = this.inscripcionForm.get('datosDeLaEmpresa') as FormGroup;
          empresaGroup.reset();
        } else {
          const empresaGroup = this.inscripcionForm.get('datosDeLaEmpresa') as FormGroup;
          const regimenControl = this.inscripcionForm.get('datosDelTitular.regimenLaboral');
          if (empresaGroup.invalid && regimenControl?.touched) {
            this.markFormGroupTouched(empresaGroup);
          }
        }
      }
    });

    this.declaracionForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.verificarEstadoSecciones();
        this.calcularProgreso();
      });

    this.checkScreenSize();
    window.addEventListener('resize', () => this.checkScreenSize());

    const phaseParam = this.route.snapshot.queryParamMap.get('phase');
    if (phaseParam === 'ddjj') {
      this.phase = 'ddjj';
      this.cargarDatosResumeDdjj();
    }

    if (this.modoAdmin) {
      this.cargarDatosMock();
      this.cargarDatosMockDDJJ();
    }

    // this.probarDesencriptacion();
  }

  createForm() {
    this.inscripcionForm = this.fb.group({
      datosDelTitular: this.fb.group({
        regimenLaboral: [null, Validators.required],
        apellido: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
        nombre: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
        tipoDocumento: [null, Validators.required],
        numeroDocumento: [null, [Validators.required, Validators.pattern(this.patronNumerico)]],
        cuil: [null, [Validators.required, Validators.pattern(this.patronCuil)]],
        fechaNacimiento: [null, Validators.required],
        sexo: [null, Validators.required],
        nacionalidad: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
        incapacitado: [null, Validators.required],
        estadoCivil: [null, Validators.required],
        telefono: [null, [Validators.required, Validators.pattern(this.patronTelefono)]],
        celular: [null, [Validators.required, Validators.pattern(this.patronTelefono)]],
        email: ['', [Validators.required, Validators.pattern(this.patronEmail)]],
        domicilio: this.fb.group({
          direccion: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoConNumeros)]],
          numero: [null, [Validators.required, Validators.pattern(this.patronNumeroDireccion)]],
          piso: [null, Validators.pattern(this.patronPiso)],
          departamento: ['', Validators.pattern(this.patronDepartamento)],
          localidad: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
          provincia: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
          codigoPostal: ['', [Validators.required, Validators.pattern(this.patronCodigoPostal)]]
        }),
        fechaIngresoEmpresa: [null, Validators.required]
      }),
      datosDeLaEmpresa: this.fb.group({
        razonSocial: ['', [Validators.required, Validators.pattern(this.patronRazonSocial)]],
        cuit: [null, [Validators.required, Validators.pattern(this.patronCuil)]],
        domicilio: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoConNumeros)]],
        telefono: [null, [Validators.required, Validators.pattern(this.patronTelefono)]]
      }),
      familiares: this.fb.array([])
    });
  }

  get familiaresFormArray(): FormArray {
    return this.inscripcionForm.get('familiares') as FormArray;
  }

  createFamiliarFormGroup(data?: any): FormGroup {
    return this.fb.group({
      parentesco: [data?.parentesco ?? null, Validators.required],
      nacionalidad: [data?.nacionalidad ?? null, Validators.required],
      incapacidad: [data?.incapacidad ?? null, Validators.required],
      nombre: [data?.nombre ?? '', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
      apellido: [data?.apellido ?? '', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
      fechaNacimiento: [data?.fechaNacimiento ?? null, Validators.required],
      sexo: [data?.sexo ?? null, Validators.required],
      cuil: [data?.cuil ?? '', [Validators.required, Validators.pattern(this.patronCuil)]],
      numeroDocumento: [data?.numeroDocumento ?? null, [Validators.required, Validators.pattern(this.patronNumerico)]],
      email: [data?.email ?? '', [Validators.pattern(this.patronEmail)]]
    });
  }

  addFamiliar() {
    this.editingFamiliarIndex = null;
    this.familiarForm.reset();
    this.showFamiliarModal = true;
  }

  editFamiliar(index: number) {
    this.editingFamiliarIndex = index;
    const familiarFormGroup = this.familiaresFormArray.at(index) as FormGroup;
    this.familiarForm.patchValue(familiarFormGroup.value);
    this.showFamiliarModal = true;
  }

  
  saveFamiliar() {
    // Limpiar cualquier error personalizado previo
    this.clearHijoError();

    if (this.familiarForm.valid) {
      const formValue = this.familiarForm.value;
      
      // Validación especial para hijos: no pueden ser mayores que el titular
      if (formValue.parentesco === Parentesco.Hijo) {
        const fechaNacimientoTitular = this.inscripcionForm.get('datosDelTitular.fechaNacimiento')?.value;
        const fechaNacimientoFamiliar = formValue.fechaNacimiento;

        if (fechaNacimientoTitular && fechaNacimientoFamiliar) {
          const titularDate = new Date(fechaNacimientoTitular);
          const familiarDate = new Date(fechaNacimientoFamiliar);

          if (familiarDate <= titularDate) {
            // Asignar error personalizado al control de fecha
            this.familiarForm.get('fechaNacimiento')?.setErrors({ hijoMayorQueTitular: true });
            // Marcar como tocado para que se muestre el error inmediatamente
            this.familiarForm.get('fechaNacimiento')?.markAsTouched();
            return; // No guardar
          }
        }
      }

      console.log('Guardando familiar con datos:', formValue);

      if (this.editingFamiliarIndex === null) {
        this.familiaresFormArray.push(this.createFamiliarFormGroup(formValue));
      } else {
        this.familiaresFormArray.at(this.editingFamiliarIndex).patchValue(formValue);
      }
      this.buildDniFileSlots();
      this.buildFamiliaresFileSlots();

      console.log('FormArray despues de guardar:', this.familiaresFormArray.value);
      this.closeFamiliarModal();
    } else {
      console.log('Formulario invalido, marcando controles');
      this.markFormGroupTouched(this.familiarForm);
      
      // Hacer scroll al primer campo con error después de un pequeño delay
      setTimeout(() => {
        this.scrollToFirstInvalidField();
      }, 100);
    }
  }

  // Nuevo método para encontrar y hacer scroll al primer campo inválido
  private scrollToFirstInvalidField(): void {
    const invalidFields = [
      'familiar-nombre',
      'familiar-apellido', 
      'familiar-parentesco',
      'familiar-nacionalidad',
      'familiar-incapacidad',
      'familiar-fechaNacimiento',
      'familiar-sexo',
      'familiar-cuil',
      'familiar-numeroDocumento',
      'familiar-email'
    ];

    for (const fieldId of invalidFields) {
      const element = document.getElementById(fieldId);
      if (element) {
        // Verificar si el campo tiene error
        const formControl = this.familiarForm.get(this.getFormControlName(fieldId));
        if (formControl && formControl.invalid && formControl.touched) {
          this.scrollTo(fieldId);
          break; // Solo scroll al primer error
        }
      }
    }
  }

  // Método auxiliar para mapear ID a formControlName
  private getFormControlName(fieldId: string): string {
    const idToControlMap: { [key: string]: string } = {
      'familiar-nombre': 'nombre',
      'familiar-apellido': 'apellido',
      'familiar-parentesco': 'parentesco',
      'familiar-nacionalidad': 'nacionalidad',
      'familiar-incapacidad': 'incapacidad',
      'familiar-fechaNacimiento': 'fechaNacimiento',
      'familiar-sexo': 'sexo',
      'familiar-cuil': 'cuil',
      'familiar-numeroDocumento' : 'numeroDocumento',
      'familiar-email' : 'email'
    };
    return idToControlMap[fieldId] || fieldId.replace('familiar-', '');
  }

  closeFamiliarModal() {
    this.showFamiliarModal = false;
    this.editingFamiliarIndex = null;
    this.familiarForm.reset();
  }

  removeFamiliar(index: number) {
    this.familiaresFormArray.removeAt(index);
    this.buildDniFileSlots();
    this.buildFamiliaresFileSlots();
  }

  nextStep(): void {
    if (this.stepValido(this.currentStep) && this.currentStep < this.steps.length) {
      this.stepErrors[this.currentStep - 1] = false;
      this.currentStep++;
      if (this.currentStep > this.maxStepReached) {
        this.maxStepReached = this.currentStep;
      }
      this.onStepEnter(this.currentStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      this.stepErrors[this.currentStep - 1] = true;
      this.markStepInvalid();
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  irAPaso(step: number): void {
    if (step <= this.maxStepReached) {
      this.currentStep = step;
    }
  }

  private onStepEnter(step: number): void {
    if (step === 6) {
      this.buildActividadFileSlots();
    }
    if (step === 7) {
      this.buildFamiliaresFileSlots();
    }
    if (step === 8) {
      this.syncSlotsToFiles();
    }
  }

  stepValido(step: number): boolean {
    switch (step) {
      case 1: {
        const t = this.inscripcionForm.get('datosDelTitular') as FormGroup;
        return t.get('apellido')?.valid! && t.get('nombre')?.valid! && t.get('tipoDocumento')?.valid!
          && t.get('numeroDocumento')?.valid! && t.get('cuil')?.valid! && t.get('fechaNacimiento')?.valid!
          && t.get('sexo')?.valid! && t.get('nacionalidad')?.valid! && t.get('incapacitado')?.valid!
          && t.get('estadoCivil')?.valid!;
      }
      case 2: {
        const t = this.inscripcionForm.get('datosDelTitular') as FormGroup;
        return t.get('telefono')?.valid! && t.get('celular')?.valid!
          && t.get('email')?.valid! && t.get('domicilio')?.valid!;
      }
      case 3: {
        const regimen = this.inscripcionForm.get('datosDelTitular.regimenLaboral')?.value;
        const fechaIngreso = this.inscripcionForm.get('datosDelTitular.fechaIngresoEmpresa')?.valid;
        if (regimen == null) return false;
        if (!fechaIngreso) return false;
        if (regimen !== RegimenLaboral.Monotributo) {
          return this.inscripcionForm.get('datosDeLaEmpresa')?.valid!;
        }
        return true;
      }
      case 4: return this.familiaresFormArray.valid;
      case 5: return this.dniFileSlots.every(s => s.file !== null);
      case 6: {
        if (this.actividadDocumentosRequeridos.length === 0) return true;
        return this.actividadFileSlots.every(s => s.file !== null);
      }
      case 7: {
        if (this.familiaresFileSlots.length === 0) return true;
        return this.familiaresFileSlots.every(s => s.file !== null);
      }
      default: return true;
    }
  }

  private markStepInvalid(): void {
    if (this.currentStep === 1 || this.currentStep === 2) {
      this.markFormGroupTouched(this.inscripcionForm.get('datosDelTitular') as FormGroup);
    }
    if (this.currentStep === 3) {
      const titular = this.inscripcionForm.get('datosDelTitular') as FormGroup;
      titular.get('regimenLaboral')?.markAsTouched();
      titular.get('fechaIngresoEmpresa')?.markAsTouched();
      this.markFormGroupTouched(this.inscripcionForm.get('datosDeLaEmpresa') as FormGroup);
    }
  }

  buildDniFileSlots(): void {
    this.dniFileSlots = [
      { label: 'Frente del DNI', descripcion: 'Titular', file: null, preview: null },
      { label: 'Dorso del DNI', descripcion: 'Titular', file: null, preview: null },
      { label: 'CODEM', descripcion: 'Código de empadronamiento', file: null, preview: null },
    ];
  }

  buildActividadFileSlots(): void {
    this.actividadFileSlots = this.actividadDocumentosRequeridos.map(doc => ({
      label: doc.documento?.nombre || 'Documento',
      descripcion: doc.documento?.descripcion || '',
      file: null,
      preview: null,
      idDoc: doc.documento?._id,
    }));
    this.selectedActividadFiles = [];
    this.actividadFilePreviews = [];
  }

  buildFamiliaresFileSlots(): void {
    this.familiaresFileSlots = [];

    const familiaresConDoc = this.getFamiliaresQueRequierenDocumentacion();
    familiaresConDoc.forEach(f => {
      const nombre = `${f.get('nombre')?.value || ''} ${f.get('apellido')?.value || ''}`.trim();
      const parentesco = f.get('parentesco')?.value;
      const parentescoLabel = parentesco === Parentesco.Hijo ? 'Hijo/a' : parentesco === Parentesco.Esposo ? 'Esposo/a' : 'Concubino/a';
      this.familiaresFileSlots.push(
        { label: 'Frente del DNI', descripcion: `${nombre} (${parentescoLabel})`, file: null, preview: null, idDoc: '60bfced9998eb202738d85e3' },
        { label: 'Dorso del DNI', descripcion: `${nombre} (${parentescoLabel})`, file: null, preview: null, idDoc: '60bfced9998eb202738d85e3' },
      );
    });

    this.familiaresFormArray.controls.forEach(f => {
      const nombre = `${f.get('nombre')?.value || ''} ${f.get('apellido')?.value || ''}`.trim();
      const parentesco = f.get('parentesco')?.value;
      const incapacidad = f.get('incapacidad')?.value;
      const fechaNacimiento = f.get('fechaNacimiento')?.value;

      let certLabel = 'Certificado';
      if (parentesco === Parentesco.Hijo) certLabel = 'Partida de nacimiento';
      else if (parentesco === Parentesco.Esposo) certLabel = 'Certificado de matrimonio';
      else if (parentesco === Parentesco.Concubino) certLabel = 'Certificado de concubinato';

      this.familiaresFileSlots.push({
        label: certLabel,
        descripcion: nombre,
        file: null, preview: null,
        idDoc: '60bfced9998eb202738d85e4'
      });

      if (fechaNacimiento && this.esMayorDeEdadFecha(fechaNacimiento)) {
        this.familiaresFileSlots.push({
          label: 'RUB', descripcion: `${nombre} (mayor de edad)`,
          file: null, preview: null, idDoc: '60bfced9998eb202738d85e4'
        });
      }

      if (parentesco === Parentesco.Hijo && fechaNacimiento && this.esMayorDe21(fechaNacimiento)) {
        this.familiaresFileSlots.push({
          label: 'Certificado de alumno regular', descripcion: nombre,
          file: null, preview: null, idDoc: '60bfced9998eb202738d85e4'
        });
      }

      if (incapacidad === true) {
        this.familiaresFileSlots.push({
          label: 'CUD', descripcion: `${nombre} (certificado de discapacidad)`,
          file: null, preview: null, idDoc: '60bfced9998eb202738d85e4'
        });
      }
    });
    this.selectedFamiliaresFiles = [];
    this.familiaresFilePreviews = [];
  }

  async onSlotFileSelected(slotIndex: number, tipo: 'dni' | 'actividad' | 'familiares', event: any): Promise<void> {
    const files: FileList = event.target.files;
    const slots = tipo === 'dni' ? this.dniFileSlots : tipo === 'actividad' ? this.actividadFileSlots : this.familiaresFileSlots;
    if (files.length > 0) {
      const rawFile = files[0];
      slots[slotIndex].error = undefined;
      if (this.validateFile(rawFile)) {
        if (this.archivoYaCargado(rawFile, slotIndex, tipo)) {
          this.mensajeArchivoDuplicado = `El archivo "${rawFile.name}" ya fue seleccionado en otro documento. Elegí un archivo distinto.`;
          this.showArchivoDuplicadoModal = true;
          event.target.value = '';
          return;
        }
        let file: File;
        try {
          const inicioCompresion = Date.now();
          file = await this.compressImage(rawFile, 1200, 1200, 0.75);
          this.inscripcionTrace.logStep(AuditoriaAccion.COMPRESION_OK, {
            paso: 'Procesamiento de imagen',
            duracionMs: Date.now() - inicioCompresion,
            payload: { archivo: rawFile.name, bytes: rawFile.size, bytesFinal: file.size },
          });
        } catch (error) {
          slots[slotIndex].error = 'No se pudo procesar la imagen. Probá con otra foto.';
          this.inscripcionTrace.logError(AuditoriaAccion.INSCRIPCION_ERROR, error, {
            paso: 'compressImage',
            payload: { archivo: rawFile.name, bytes: rawFile.size },
          });
          event.target.value = '';
          return;
        }
        const previewAnterior = slots[slotIndex].preview;
        if (previewAnterior?.isImage && typeof previewAnterior.url === 'string' && previewAnterior.url.startsWith('blob:')) {
          URL.revokeObjectURL(previewAnterior.url);
        }
        slots[slotIndex].file = file;
        slots[slotIndex].uploaded = false;
        this.generateSlotPreview(slots[slotIndex], file);
        this.syncSlotsToFiles();
      } else {
        // Error visible sobre el slot: el archivo rechazado no queda "cargado" en silencio.
        slots[slotIndex].error = this.mensajeErrorArchivo(rawFile);
      }
    }
    event.target.value = '';
  }

  private mensajeErrorArchivo(file: File): string {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence',
    ];
    const esHeic = /\.(heic|heif)$/i.test(file.name);
    if (!allowedTypes.includes(file.type) && !esHeic) {
      return 'Tipo de archivo no permitido. Solo PDF, JPG, PNG o HEIC.';
    }
    if (file.size > 50 * 1024 * 1024) {
      return 'El archivo excede el tamaño máximo permitido de 50 MB.';
    }
    return 'No se pudo usar este archivo. Elegí otro.';
  }

  private archivoYaCargado(file: File, slotIndex: number, tipo: 'dni' | 'actividad' | 'familiares'): boolean {
    const todos = [...this.dniFileSlots, ...this.actividadFileSlots, ...this.familiaresFileSlots];
    const target = (tipo === 'dni' ? this.dniFileSlots : tipo === 'actividad' ? this.actividadFileSlots : this.familiaresFileSlots)[slotIndex];
    return todos.some(slot => {
      if (slot === target) return false;
      const f = slot.file;
      if (!f) return false;
      return f.name === file.name && f.size === file.size;
    });
  }

  removeSlotFile(slotIndex: number, tipo: 'dni' | 'actividad' | 'familiares'): void {
    const slots = tipo === 'dni' ? this.dniFileSlots : tipo === 'actividad' ? this.actividadFileSlots : this.familiaresFileSlots;
    const preview = slots[slotIndex].preview;
    if (preview?.isImage && preview.url) {
      URL.revokeObjectURL(preview.url as string);
    }
    slots[slotIndex].file = null;
    slots[slotIndex].preview = null;
    slots[slotIndex].error = undefined;
    slots[slotIndex].uploaded = false;
    this.syncSlotsToFiles();
  }

  private compressImage(file: File, maxWidth: number, maxHeight: number, quality: number): Promise<File> {
    return new Promise((resolve) => {
      let settled = false;
      const done = (resultado: File) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutId);
        resolve(resultado);
      };
      // Red de seguridad: sin esto la selección puede quedar colgada para siempre.
      const timeoutId = setTimeout(() => {
        console.warn('compressImage: tiempo de espera agotado, se usa el archivo original');
        done(file);
      }, 15000);

      if (!file.type.startsWith('image/')) {
        done(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target!.result as string;

        img.onload = () => {
          if (img.width <= maxWidth && img.height <= maxHeight && file.size < 512 * 1024) {
            img.src = '';
            done(file);
            return;
          }

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            img.src = '';
            done(file);
            return;
          }

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round(height * maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round(width * maxHeight / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          img.src = '';

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const outputName = file.name;
                const compressedFile = new File([blob], outputName, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                canvas.width = 0;
                canvas.height = 0;
                done(compressedFile);
              } else {
                canvas.width = 0;
                canvas.height = 0;
                done(file);
              }
            },
            'image/jpeg',
            quality,
          );
        };

        img.onerror = () => {
          img.src = '';
          done(file);
        };
      };

      reader.onerror = () => {
        done(file);
      };

      reader.readAsDataURL(file);
    });
  }

  private generateSlotPreview(slot: FileSlot, file: File): void {
    const esHeic =
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      file.type === 'image/heic-sequence' ||
      file.type === 'image/heif-sequence' ||
      /\.hei[cf]$/i.test(file.name);

    if (esHeic) {
      // Chrome no decodifica HEIC; se muestra un icono placeholder y el
      // backend lo convierte a WebP al subir.
      const placeholder =
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
            '<rect width="100" height="100" fill="#f1f5f9"/>' +
            '<path d="M22 22h56v56H22z" fill="none" stroke="#B1476A" stroke-width="4"/>' +
            '<circle cx="40" cy="42" r="7" fill="#4CA7C8"/>' +
            '<path d="M22 74l22-16 14 10 12-14 8 7v13H22z" fill="#4CA7C8"/>' +
            '</svg>'
        );
      slot.preview = {
        url: placeholder,
        safeUrl: this.sanitizer.bypassSecurityTrustUrl(placeholder),
        name: file.name,
        type: file.type,
        isImage: true,
      };
    } else if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      slot.preview = {
        url,
        safeUrl: this.sanitizer.bypassSecurityTrustUrl(url),
        name: file.name,
        type: file.type,
        isImage: true,
      };
    } else {
      slot.preview = {
        url: '',
        name: file.name,
        type: file.type,
        isImage: false,
      };
    }
  }

  syncSlotsToFiles(): void {
    this.selectedDniFiles = this.dniFileSlots.filter(s => s.file).map(s => s.file!);
    this.dniFilePreviews = this.dniFileSlots.filter(s => s.preview).map(s => s.preview!);

    this.selectedActividadFiles = this.actividadFileSlots.filter(s => s.file).map(s => s.file!);
    this.actividadFilePreviews = this.actividadFileSlots.filter(s => s.preview).map(s => s.preview!);

    this.selectedFamiliaresFiles = this.familiaresFileSlots.filter(s => s.file).map(s => s.file!);
    this.familiaresFilePreviews = this.familiaresFileSlots.filter(s => s.preview).map(s => s.preview!);
  }

  countFilledSlots(slots: FileSlot[]): number {
    return slots.filter(s => s.file !== null).length;
  }

  async onSubmit() {
    // Evita re-abrir el modal de confirmación mientras ya hay uno abierto o un envío corriendo.
    if (this.flagRegistrando || this.showConfirmarAfiliacionModal) {
      return;
    }
    this.syncSlotsToFiles();

    const regimen = this.inscripcionForm.get('datosDelTitular.regimenLaboral')?.value;
    const esMonotributo = regimen === RegimenLaboral.Monotributo;

    // Validar solo las secciones relevantes
    const datosTitularValidos = this.inscripcionForm.get('datosDelTitular')?.valid;
    const familiaresValidos = this.inscripcionForm.get('familiares')?.valid;
    const empresaValida = esMonotributo
      ? true
      : this.inscripcionForm.get('datosDeLaEmpresa')?.valid;

    const minFilesRequired = this.getMinRequiredFiles();
    const hasRequiredFiles = this.validateFileRequirements();
    const hasRequiredActividadFiles = this.validateActividadFileRequirements();
    const hasRequiredFamiliaresFiles = this.validateFamiliaresFileRequirements();

    if (
      datosTitularValidos &&
      familiaresValidos &&
      empresaValida &&
      hasRequiredFiles &&
      hasRequiredActividadFiles &&
      hasRequiredFamiliaresFiles
    ) {
      this.showConfirmarAfiliacionModal = true;
      return;
    } else {
      // Marcar errores en el formulario
      this.markFormGroupTouched(this.inscripcionForm.get('datosDelTitular') as FormGroup);
      this.markFormGroupTouched(this.inscripcionForm.get('familiares') as FormArray);
      if (!esMonotributo) {
        this.markFormGroupTouched(this.inscripcionForm.get('datosDeLaEmpresa') as FormGroup);
      }
      if (!hasRequiredFiles) {
        this.fileUploadError = this.getFileRequirementMessage();
      }
      if (!hasRequiredActividadFiles) {
        this.actividadFileUploadError = this.getActividadFileRequirementMessage();
      }
      if (!hasRequiredFamiliaresFiles) {
        this.familiaresFileUploadError = this.getFamiliaresFileRequirementMessage();
      }
    }
  }

  // Función modular para mapear solo los datos disponibles
  private mapearDatosParaRegister(): any {
    const formData = this.inscripcionForm.value;
    
    return {
      nombrePersona: formData.datosDelTitular?.nombre || '',
      apellidoPersona: formData.datosDelTitular?.apellido || '',
      nickname: `${formData.datosDelTitular?.nombre || ''} ${formData.datosDelTitular?.apellido || ''}`.trim(),
      email: formData.datosDelTitular?.email || '',
      emailConfirm: formData.datosDelTitular?.email || '',
      perfil: '65dccf3ac186b433577b3ae6', // perfil GENERAL estático
      password: formData.datosDelTitular?.numeroDocumento,
      passwordConfirm: formData.datosDelTitular?.numeroDocumento,
      fromWeb: true,
      fromApp: false,
      signoSedes: ['689e41b9bf64830c3ec6a613'], // id sede INSCRIPCION estático
      aceptaTerminos: true,
      proteccionDeDatos: true,
      clausula: true,
      notifica: false,
      identificacion: formData.datosDelTitular?.numeroDocumento,
      telfijo: formData.datosDelTitular?.telefono || '',
      telcelular: formData.datosDelTitular?.celular || '',
      estadoCivil: formData.datosDelTitular?.estadoCivil || null,
      // Reuse opt-in: el backend decide si reutiliza la proto-cuenta (limbo)
      // en vez de pisarla. Solo esta ruta lo activa.
      reuseProtoCuenta: true
    };
  }

  private logFormErrors(formGroup: FormGroup | FormArray, prefix: string = '') {
    const errors: {[key: string]: any} = {};
    
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.controls[key];
      if (control instanceof FormGroup || control instanceof FormArray) {
        const nestedErrors = this.logFormErrors(control, `${prefix}${key}.`);
        if (Object.keys(nestedErrors).length > 0) {
          errors[key] = nestedErrors;
        }
      } else {
        if (control.errors) {
          errors[`${prefix}${key}`] = control.errors;
        }
      }
    });
    
    if (Object.keys(errors).length > 0 && prefix === '') {
      console.log('Errores de validación:', errors);
    }
    
    return errors;
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.controls[key];
      control.markAsTouched();
      
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // datos mock
  private cargarDatosMock(): void {
    const datosMock = {
      datosDelTitular: {
        regimenLaboral: null, // General
        apellido: 'Titular',
        nombre: 'Ejemplo', 
        tipoDocumento: 0, // DNI
        numeroDocumento: '12345678',
        cuil: '20123456786',
        fechaNacimiento: new Date(),
        sexo: 0, // Masculino
        nacionalidad: 'Argentino',
        incapacitado: false,
        estadoCivil: 0, // Soltero
        telefono: '12341234',
        celular: '12331233',
        email: 'elgopnik64@gmail.com',
        domicilio: {
          direccion: 'Ejemplo',
          numero: '123',
          piso: '1',
          departamento: 'A',
          localidad: 'Localidad',
          provincia: 'Provincia',
          codigoPostal: '1234X'
        },
        fechaIngresoEmpresa: '2025-09-22T03:00:00.000Z'
      },
      datosDeLaEmpresa: {
        razonSocial: 'Razon Social',
        cuit: '12345678',
        domicilio: 'Ejemplo 123',
        telefono: '12351235'
      },
      familiares: []
    };

    this.inscripcionForm.patchValue(datosMock);
    console.log('MODO ADMIN: Datos de prueba cargados');
  }

  private obtenerActividadIdPorRegimen(regimenLaboral: number): string {
    switch (regimenLaboral) {
      case 0: // RegimenLaboral.General
        return '68c86b61e6c4fc16024be451';
      case 1: // RegimenLaboral.Monotributo
        return '68c9c61e99bb6b0adb671593';
      case 2: // RegimenLaboral.ServicioDomestico
        return '68c9c64799bb6b0adb6715c8';
      default:
        console.error('Régimen laboral no reconocido:', regimenLaboral);
        return '';
    }
  }

  private crearObjetoSolicitud(actividadId: string, userId: string, numberState: number, inscripcionData: any): any {
    const solicitud = {
      pais: this.iAppOfertas.paisDefault,
      actividad: actividadId,
      state: { numberState: numberState },
      type: 'OFERTA-LABORAL',
      usuario: userId,
      titular: userId,
      beneficiario: userId,
      signoSede: '689e41b9bf64830c3ec6a613',
      inscripcion: {
        contenido: inscripcionData,
        acepta: true
        //timeMark ? 
      }
    };
    
    console.log('Solicitud con inscripción:', solicitud);
    return solicitud;
  }

  // Método actualizado para manejar múltiples archivos
  onDniFileSelected(event: any): void {
    const files: FileList = event.target.files;
    this.fileUploadError = ''; // Limpiar error al agregar archivos
    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (this.validateFile(file)) {
          this.selectedDniFiles.push(file);
          this.generateFilePreview(file);
        }
      }
    }
  }

  // Método para eliminar archivo específico (por índice)
  removeDniFile(index: number): void {
    this.fileUploadError = ''; // Limpiar error al eliminar archivos
    this.selectedDniFiles.splice(index, 1);
    this.dniFilePreviews.splice(index, 1);
  }

  // Método actualizado para generar previews
  private generateFilePreview(file: File): void {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        this.dniFilePreviews.push({
          url: reader.result!,
          name: file.name,
          type: file.type,
          isImage: true
        });
      };
      reader.readAsDataURL(file);
    } else {
      // Para PDFs, agregar sin preview pero con información
      this.dniFilePreviews.push({
        url: '',
        name: file.name,
        type: file.type,
        isImage: false
      });
    }
  }

  // Método para subir todos los archivos (resiliente: acumula fallos, no aborta)
  private async uploadDniFiles(solicitudCreada: any): Promise<void> {
    await this.subirGrupoPendiente(solicitudCreada, 'dni');
  }

  async procesarInscripcion(): Promise<void> {
    // Single-flight: un segundo click/Enter durante el envío no re-entra.
    if (this.flagRegistrando) {
      return;
    }
    this.showConfirmarAfiliacionModal = false;

    // Si en esta sesión ya se creó una solicitud (upload fallido/cancelado),
    // se continúa ESA solicitud: no se re-registra ni se crea otra.
    if (this.solicitudPendienteId) {
      await this.reanudarSubidaExistente();
      return;
    }

    this.inscripcionTrace.nuevoIntento();
    const emailForm = this.inscripcionForm.get('datosDelTitular.email')?.value;
    this.inscripcionTrace.setEmail(typeof emailForm === 'string' ? emailForm.toLowerCase().trim() : null);
    this.inscripcionTrace.logStep(AuditoriaAccion.INSCRIPCION_INICIO, {
      paso: 'Inicio de inscripción',
      payload: { regimen: this.inscripcionForm.get('datosDelTitular.regimenLaboral')?.value },
    });

    this.flagRegistrando = true;
    this.isUploadingFile = true;
    this.progresoSubida = null;
    this.uploadTotal = 0;
    this.uploadCompletos = 0;
    this.textoProgreso = 'Registrando su inscripción, por favor espere';

    // Si quedó basura de un intento anterior, se descarta antes de empezar.
    this.storageService.removeInscripcionOsData();

    // Si quedó basura de un intento anterior, se descarta antes de empezar.
    this.storageService.removeInscripcionOsData();

    const regimen = this.inscripcionForm.get('datosDelTitular.regimenLaboral')?.value;

    try {
      const formData: InscripcionOs = this.inscripcionForm.value;

      const emailControlValue = this.inscripcionForm.get('datosDelTitular.email')?.value;
      const email = typeof emailControlValue === 'string' ? emailControlValue.toLowerCase().trim() : '';
      this.inscripcionTrace.setEmail(email || null);

      let userId: string;

      if (email) {
        const solicitudCheck = await this.checkExistingSolicitudByEmail(email);

        // Solicitud en curso (no anulada): no se re-registra.
        if (solicitudCheck.exists && !solicitudCheck.hasAnnulledSolicitud) {
          this.guiMsjService.msjFormSubmit(
            'Ya posee una solicitud de inscripción en curso. Revise su correo o espere la resolución.'
          );
          this.flagRegistrando = false;
          return;
        }
      }

      // Registro o reuse de la proto-cuenta: la decisión es server-side
      // (register con reuseProtoCuenta). Se eliminó el updateUser anónimo.
      const datosRegister = this.mapearDatosParaRegister();
      const registroExitoso = await this.authService.registerInscripcionOs(datosRegister, true);
      if (!registroExitoso.success) {
        this.inscripcionTrace.logError(
          AuditoriaAccion.REGISTRO_ERROR,
          registroExitoso.error || new Error('registerInscripcionOs devolvió success=false'),
          { paso: 'Registro de usuario' },
        );
        this.mostrarErrorRegistroInscripcion(registroExitoso.error);
        this.flagRegistrando = false;
        return;
      }
      userId = registroExitoso.userId as string;

      if (registroExitoso.reused) {
        this.inscripcionTrace.logStep(AuditoriaAccion.RECUPERACION_CUENTA, {
          paso: 'Proto-cuenta reutilizada',
          payload: { userId },
        });
        this.guiMsjService.msjFormSubmit(
          'Se ha recuperado su cuenta anterior para iniciar una nueva inscripción.'
        );
      }

      this.inscripcionTrace.setUsuarioId(userId);
      this.inscripcionTrace.logStep(AuditoriaAccion.REGISTRO_OK, { paso: 'Usuario registrado', payload: { userId } });

      const actividadId = this.obtenerActividadIdPorRegimen(regimen);
      const solicitudInscripcion = this.crearObjetoSolicitud(actividadId, userId, 14, formData);
      const inicioSolicitud = Date.now();
      let solicitudCreada: any;
      try {
        solicitudCreada = await this.solicitudesService.createEncrypted(solicitudInscripcion);
      } catch (error) {
        this.inscripcionTrace.logError(AuditoriaAccion.SOLICITUD_ERROR, error, {
          paso: 'createEncrypted solicitud',
          duracionMs: Date.now() - inicioSolicitud,
        });
        throw error;
      }
      this.inscripcionTrace.setSolicitudId(solicitudCreada._id);
      this.inscripcionTrace.logStep(AuditoriaAccion.SOLICITUD_CREADA, {
        paso: 'Solicitud creada (estado 14)',
        duracionMs: Date.now() - inicioSolicitud,
        payload: { solicitudId: solicitudCreada._id, actividadId },
      });

      // A partir de acá la solicitud YA existe en el backend: se guarda el id de
      // inmediato (memoria + seam) por si un upload falla y hay que reintentarlo
      // sobre la MISMA solicitud, sin quedar varado.
      this.solicitudId = solicitudCreada._id;
      this.solicitudPendienteId = solicitudCreada._id;
      this.inscripcionData = formData;
      this.prefillDatosPersonalesDdjj(formData);
      await this.storageService.setInscripcionOsData({
        inscripcion: formData,
        solicitudId: this.solicitudId,
      });

      // Los uploads ya no abortan: acumulan en subidasFallidas los que fallaron.
      this.subidasFallidas = [];
      this.iniciarProgresoSubida();
      await this.uploadDniFiles(solicitudCreada);
      await this.uploadActividadFiles(solicitudCreada);
      await this.uploadFamiliaresFiles(solicitudCreada);

      if (this.subidasFallidas.length > 0) {
        this.showSubidaFallidaModal = true;
        return;
      }

      await this.finalizarHaciaDdjj();
    } catch (error) {
      console.error('Error en el registro:', error);
      this.inscripcionTrace.logError(AuditoriaAccion.INSCRIPCION_ERROR, error, { paso: 'procesarInscripcion' });
      if (error && error.message && error.message.includes('subir')) {
        return; // Ya se mostró el modal de archivos
      }
      this.showRegistroErrorModal = true;
    } finally {
      this.flagRegistrando = false;
      this.isUploadingFile = false;
      // Recién cuando el paso 1 de la DDJJ está montado se apaga el overlay.
      if (this.phase === 'ddjj') {
        this.revelarPaso1Ddjj();
      }
    }
  }

  private obtenerSlotsPorTipo(tipo: 'dni' | 'actividad' | 'familiares'): FileSlot[] {
    return tipo === 'dni' ? this.dniFileSlots : tipo === 'actividad' ? this.actividadFileSlots : this.familiaresFileSlots;
  }

  obtenerSlotFallido(f: SubidaFallida): FileSlot | null {
    const slots = this.obtenerSlotsPorTipo(f.tipo);
    return slots[f.slotIndex] || null;
  }

  private idDocParaSlot(tipo: 'dni' | 'actividad' | 'familiares', slotIndex: number): string {
    const slot = this.obtenerSlotsPorTipo(tipo)[slotIndex];
    if (slot?.idDoc) {
      return slot.idDoc;
    }
    if (tipo === 'actividad') {
      return this.actividadDocumentosRequeridos[slotIndex]?.documento?._id || '60bfced9998eb202738d85e3';
    }
    return '60bfced9998eb202738d85e3';
  }

  /** Prepara la barra de progreso para la subida de documentos pendientes. */
  private iniciarProgresoSubida(): void {
    this.limpiarAvisoSubida();
    const grupos = [this.dniFileSlots, this.actividadFileSlots, this.familiaresFileSlots];
    this.uploadTotal = grupos.reduce(
      (acc, g) => acc + g.filter((s) => s.file && !s.uploaded).length,
      0
    );
    this.uploadCompletos = 0;
    this.uploadArchivoPct = 0;
    this.progresoSubida = this.uploadTotal > 0 ? 0 : null;
    this.actualizarTextoProgreso();
  }

  private onAvanceSubida(resp: any): void {
    if (!this.isUploadingFile || this.uploadTotal === 0) {
      return;
    }
    const value = Number(resp?.value);
    if (isNaN(value)) {
      return;
    }
    this.uploadArchivoPct = Math.max(0, Math.min(100, value));
    this.recalcularProgresoSubida();
  }

  private recalcularProgresoSubida(): void {
    if (this.uploadTotal === 0) {
      this.progresoSubida = null;
      return;
    }
    const hecho = this.uploadCompletos + this.uploadArchivoPct / 100;
    this.progresoSubida = Math.max(0, Math.min(100, Math.round((hecho / this.uploadTotal) * 100)));
    this.actualizarTextoProgreso();
  }

  private actualizarTextoProgreso(): void {
    this.textoProgreso = this.uploadTotal > 0
      ? `Subiendo documentos ${this.uploadCompletos}/${this.uploadTotal} — ${this.progresoSubida ?? 0}%`
      : 'Procesando su inscripción, por favor espere';
  }

  /** Avisa si una subida viene lenta (por tiempo, sin depender de la Network API). */
  private iniciarAvisoSubidaLenta(): void {
    this.limpiarAvisoSubida();
    this.avisoSubidaTimer = setTimeout(() => {
      this.avisoSubida = 'Su conexión parece lenta. La subida puede demorar; por favor, no cierre esta ventana.';
      this.avisoSubidaTimer = setTimeout(() => {
        this.avisoSubida = 'Conexión muy lenta, por favor no cierre esta ventana.';
      }, 22000);
    }, 8000);
  }

  private limpiarAvisoSubida(): void {
    if (this.avisoSubidaTimer) {
      clearTimeout(this.avisoSubidaTimer);
      this.avisoSubidaTimer = undefined;
    }
    this.avisoSubida = '';
  }

  // Sube los slots pendientes (con archivo y sin uploaded=true) de un grupo.
  // NO aborta: cada fallo queda marcado en subidasFallidas y en el slot.
  private async subirGrupoPendiente(solicitud: any, tipo: 'dni' | 'actividad' | 'familiares'): Promise<void> {
    const slots = this.obtenerSlotsPorTipo(tipo);
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!slot.file || slot.uploaded) {
        continue;
      }
      const inicioSubida = Date.now();
      this.uploadArchivoPct = 0;
      this.recalcularProgresoSubida();
      this.iniciarAvisoSubidaLenta();
      const metaArchivo = { tipo, label: slot.label, archivo: slot.file.name, bytes: slot.file.size };
      try {
        // Gancho DEV (modoAdmin): fuerza un falso error UNA sola vez para probar la re-subida.
        if (this.modoAdmin && this.simularErrorSubidaAdmin) {
          this.simularErrorSubidaAdmin = false;
          throw new Error('FALLO SIMULADO (modoAdmin): error forzado para probar la re-subida');
        }
        await this.documentosService.uploadDocumentoAdjuntoEncrypted(
          slot.file,
          solicitud,
          this.idDocParaSlot(tipo, i)
        );
        slot.uploaded = true;
        slot.error = undefined;
        console.log('✅ Archivo subido exitosamente:', slot.label, slot.file.name);
        this.inscripcionTrace.logStep(AuditoriaAccion.UPLOAD_OK, {
          paso: `Subida ${tipo}`,
          duracionMs: Date.now() - inicioSubida,
          payload: metaArchivo,
        });
      } catch (error) {
        console.error('❌ Error al subir archivo:', slot.label, slot.file?.name, error);
        slot.error = 'No se pudo subir este archivo. Elegilo de nuevo o reintentá la subida.';
        this.inscripcionTrace.logError(AuditoriaAccion.UPLOAD_ERROR, error, {
          paso: `Subida ${tipo}`,
          duracionMs: Date.now() - inicioSubida,
          payload: metaArchivo,
        });
        this.subidasFallidas.push({
          tipo,
          slotIndex: i,
          label: slot.label,
          archivo: slot.file?.name || '',
        });
      }
      this.uploadCompletos++;
      this.uploadArchivoPct = 0;
      this.limpiarAvisoSubida();
      this.recalcularProgresoSubida();
    }
  }

  private async finalizarHaciaDdjj(): Promise<void> {
    // Salto directo a la DDJJ bajo el overlay: no queda formulario de
    // inscripción vivo detrás que el usuario pueda tocar.
    this.inscripcionTrace.logStep(AuditoriaAccion.INSCRIPCION_FIN, {
      paso: 'Inscripción completada, salto a DDJJ',
      payload: { solicitudId: this.solicitudId },
    });
    this.avisoPaso1Completado = true;
    this.isUploadingDDJJ = true;
    this.phase = 'ddjj';
    await this.router.navigate([], {
      queryParams: { phase: 'ddjj' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // Reintenta la subida de los archivos pendientes sobre la MISMA solicitud
  // (nunca re-registra ni crea otra). Usado desde la modal de fallo y desde
  // "Enviar inscripción" cuando en esta sesión ya existe una solicitud creada.
  private async reanudarSubidaExistente(): Promise<void> {
    if (this.flagRegistrando) {
      return;
    }
    this.showSubidaFallidaModal = false;
    this.flagRegistrando = true;
    this.isUploadingFile = true;
    this.progresoSubida = null;
    this.uploadTotal = 0;
    this.uploadCompletos = 0;
    this.textoProgreso = 'Reanudando la subida de documentos, por favor espere';
    try {
      this.inscripcionTrace.setSolicitudId(this.solicitudPendienteId);
      this.inscripcionTrace.logStep(AuditoriaAccion.INSCRIPCION_INICIO, {
        paso: 'Reanudar subida sobre solicitud existente',
        payload: { solicitudId: this.solicitudPendienteId },
      });
      const solicitudExistente = await this.solicitudesService.getSolicitudByIdEncrypted(this.solicitudPendienteId!);
      const formData: InscripcionOs = this.inscripcionForm.value;
      this.inscripcionData = formData;
      this.prefillDatosPersonalesDdjj(formData);
      await this.storageService.setInscripcionOsData({
        inscripcion: formData,
        solicitudId: this.solicitudPendienteId!,
      });

      this.subidasFallidas = [];
      this.iniciarProgresoSubida();
      await this.uploadDniFiles(solicitudExistente);
      await this.uploadActividadFiles(solicitudExistente);
      await this.uploadFamiliaresFiles(solicitudExistente);

      if (this.subidasFallidas.length > 0) {
        this.showSubidaFallidaModal = true;
        return;
      }
      await this.finalizarHaciaDdjj();
    } catch (error) {
      console.error('Error al reanudar la subida:', error);
      this.inscripcionTrace.logError(AuditoriaAccion.INSCRIPCION_ERROR, error, { paso: 'reanudarSubidaExistente' });
      this.showRegistroErrorModal = true;
    } finally {
      this.flagRegistrando = false;
      this.isUploadingFile = false;
      if (this.phase === 'ddjj') {
        this.revelarPaso1Ddjj();
      }
    }
  }

  reintentarSubidasFallidas(): void {
    this.reanudarSubidaExistente();
  }

  toggleSimularErrorSubida(): void {
    this.simularErrorSubidaAdmin = !this.simularErrorSubidaAdmin;
  }

  private revelarPaso1Ddjj(): void {
    setTimeout(() => {
      this.isUploadingDDJJ = false;
      this.ddjjStep = 1;
      this.ddjjStepErrors = Array(10).fill(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 400);
  }

  cerrarAvisoPaso1(): void {
    this.avisoPaso1Completado = false;
  }

  private mostrarErrorDdjj(mensaje: string): void {
    this.ddjjErrorMensaje = this.conReferencia(mensaje);
    this.showDdjjErrorModal = true;
  }

  /** Agrega el código de referencia de la traza para soporte. */
  conReferencia(mensaje: string): string {
    const ref = this.inscripcionTrace.getReferencia();
    return ref
      ? `${mensaje}<br><br><small>Código de referencia: <b>${ref}</b></small>`
      : mensaje;
  }

  /** Mensaje claro según el motivo real que devolvió el backend. */
  private mostrarErrorRegistroInscripcion(error: any): void {
    const message = error && typeof error.message === 'string' ? error.message : '';
    if (message.indexOf('CUENTA_VALIDADA') !== -1) {
      this.guiMsjService.msjFormSubmit(
        'El DNI ya pertenece a una cuenta activa. No es posible iniciar una nueva inscripción con estos datos. Contacte a la obra social.'
      );
    } else if (message.indexOf('DNI_EXISTENTE') !== -1 || message.indexOf('EMAIL_EXISTENTE') !== -1) {
      this.guiMsjService.msjFormSubmit(
        'Los datos ingresados ya están registrados. Verifique el DNI y el email, o contacte a la obra social.'
      );
    } else {
      this.guiMsjService.msjFormSubmit(
        'No se pudo completar el registro. Intente nuevamente en unos minutos.'
      );
    }
  }

  // Métodos auxiliares para el template
  getFileIcon(fileType: string): string {
    if (fileType === 'application/pdf') return 'picture_as_pdf';
    if (fileType.startsWith('image/')) return 'image';
    return 'insert_drive_file';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private validateFile(file: File): boolean {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence',
    ];
    const esHeic = /\.(heic|heif)$/i.test(file.name);
    const maxSize = 50 * 1024 * 1024;

    if (!allowedTypes.includes(file.type) && !esHeic) {
      this.guiMsjService.msjFormSubmit('Tipo de archivo no permitido. Solo PDF, JPG, PNG o HEIC.');
      return false;
    }

    if (file.size > maxSize) {
      this.showFileSizeError = true;
      return false;
    }

    return true;
  }

  getMinRequiredFiles(): number {
    return 3;
  }

  getFamiliaresQueRequierenDocumentacion(): any[] {
    const familiaresConDoc = [];
    
    this.familiaresFormArray.controls.forEach(familiar => {
      const parentesco = familiar.get('parentesco')?.value;
      
      // Solo estos parentescos requieren documentación de DNI
      if (parentesco === Parentesco.Hijo || 
          parentesco === Parentesco.Esposo || 
          parentesco === Parentesco.Concubino) {
        familiaresConDoc.push(familiar);
      }
    });
    
    return familiaresConDoc;
  }

  validateFileRequirements(): boolean {
    const minRequired = this.getMinRequiredFiles();
    return this.selectedDniFiles.length >= minRequired;
  }

  getFileRequirementMessage(): string {
    return this.getDniCodemRequirementDescription();
  }

  //MODAL
  sanitizeUrl(url: string | ArrayBuffer): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url as string);
  }

  openImageModal(imageUrl: string | ArrayBuffer): void {
    this.selectedImageForModal = imageUrl as string;
  }
  closeImageModal(): void {
    this.selectedImageForModal = null;
  }

  // Agregar estos métodos en la clase

  getValidationMessage(): string {
    return this.getDniCodemValidationMessage();
  }

  initFamiliarForm() {
    this.familiarForm = this.createFamiliarFormGroup();
    
    // Limpiar error personalizado cuando cambia parentesco o fecha
    this.familiarForm.get('parentesco')?.valueChanges.subscribe(() => {
      this.clearHijoError();
    });
    this.familiarForm.get('fechaNacimiento')?.valueChanges.subscribe(() => {
      this.clearHijoError();
    });
  }

  private clearHijoError(): void {
    const fechaControl = this.familiarForm.get('fechaNacimiento');
    if (fechaControl?.hasError('hijoMayorQueTitular')) {
      fechaControl.setErrors(null);
      const errors = { ...fechaControl.errors };
      delete errors['hijoMayorQueTitular'];
      fechaControl.setErrors(Object.keys(errors).length ? errors : null);
    }
  }


  scrollTo(seccionId: string): void {
    const element = document.getElementById(seccionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  private async cargarActividadPorRegimen(regimen: RegimenLaboral): Promise<void> {
    try {

      this.selectedActividadFiles = [];
      this.actividadFilePreviews = [];
      this.actividadFileUploadError = '';

      const actividadId = this.obtenerActividadIdPorRegimen(regimen);
      if (actividadId) {
        this.actividadSeleccionada = await this.actividadesService.getActividadesFindOneEncrypted(actividadId);
        const docsRaw = this.actividadSeleccionada?.documentosEstadosSolicitar;
        this.actividadDocumentosRequeridos = docsRaw || [];
        
        // Filtrar para excluir el DNI que ya manejamos por separado
        this.actividadDocumentosRequeridos = this.actividadDocumentosRequeridos.filter(doc => 
          doc.documento._id !== '60bfced9998eb202738d85e3'
        );
        
        this.buildActividadFileSlots();
      }
    } catch (error) {
      console.error('Error al cargar actividad:', error);
      this.inscripcionTrace.logError(AuditoriaAccion.INSCRIPCION_ERROR, error, {
        paso: 'cargarActividadPorRegimen',
      });
      this.actividadDocumentosRequeridos = [];
    }
  }

  onActividadFileSelected(event: any): void {
    const files: FileList = event.target.files;
    this.actividadFileUploadError = '';
    
    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (this.validateFile(file)) {
          this.selectedActividadFiles.push(file);
          this.generateActividadFilePreview(file);
        }
      }
    }
  }

  removeActividadFile(index: number): void {
    this.actividadFileUploadError = '';
    this.selectedActividadFiles.splice(index, 1);
    this.actividadFilePreviews.splice(index, 1);
  }

  private generateActividadFilePreview(file: File): void {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        this.actividadFilePreviews.push({
          url: reader.result!,
          name: file.name,
          type: file.type,
          isImage: true
        });
      };
      reader.readAsDataURL(file);
    } else {
      this.actividadFilePreviews.push({
        url: '',
        name: file.name,
        type: file.type,
        isImage: false
      });
    }
  }

  private async uploadActividadFiles(solicitudCreada: any): Promise<void> {
    await this.subirGrupoPendiente(solicitudCreada, 'actividad');
  }

  // Nuevos métodos de validación para actividad
  getMinActividadRequiredFiles(): number {
    return this.actividadDocumentosRequeridos.length;
  }

  validateActividadFileRequirements(): boolean {
    // Si no hay documentos requeridos, no es necesario subir archivos
    if (this.actividadDocumentosRequeridos.length === 0) {
      return true;
    }
    return this.selectedActividadFiles.length >= this.getMinActividadRequiredFiles();
  }

  getActividadFileRequirementMessage(): string {
    const minRequired = this.getMinActividadRequiredFiles();
    const currentFiles = this.selectedActividadFiles.length;
    
    if (minRequired === 0) {
      return 'No se requieren documentos adicionales para este régimen.';
    }
    
    if (currentFiles === 0) {
      return `Debe subir ${minRequired} archivos para la documentación adicional del ${this.getRegimenLaboralNombre()}.`;
    }
    
    if (!this.validateActividadFileRequirements()) {
      return `Documentación adicional incompleta. Requeridos: ${minRequired}, Subidos: ${currentFiles}.`;
    }
    
    return `Documentación adicional completa (${currentFiles}/${minRequired} archivos)`;
  }

  getActividadValidationMessage(): string {
    const totalRequeridos = this.getMinActividadRequiredFiles();
    const archivosSubidos = this.selectedActividadFiles.length;
    
    if (totalRequeridos === 0) {
      return 'No se requieren documentos adicionales';
    }
    
    if (archivosSubidos === 0) {
      return `No se han subido archivos. ${this.getActividadRequirementDescription()}`;
    }
    
    if (archivosSubidos < totalRequeridos) {
      const faltan = totalRequeridos - archivosSubidos;
      return `Faltan ${faltan} archivo${faltan !== 1 ? 's' : ''}. ${this.getActividadRequirementDescription()}. Archivos subidos: ${archivosSubidos}`;
    }
    
    return `Documentación adicional completa (${archivosSubidos}/${totalRequeridos} archivos)`;
  }

  public getRegimenLaboralNombre(): string {
    const regimen = this.inscripcionForm.get('datosDelTitular.regimenLaboral')?.value;
    switch (regimen) {
      case RegimenLaboral.General: return 'Relación de Dependencia';
      case RegimenLaboral.Monotributo: return 'Monotributo';
      case RegimenLaboral.ServicioDomestico: return 'Servicio Doméstico';
      default: return '';
    }
  }

  // Método para manejar selección de archivos de familiares
  onFamiliaresFileSelected(event: any): void {
    const files: FileList = event.target.files;
    this.familiaresFileUploadError = '';
    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (this.validateFile(file)) {
          this.selectedFamiliaresFiles.push(file);
          this.generateFamiliaresFilePreview(file);
        }
      }
    }
  }

  // Método para eliminar archivo de familiares
  removeFamiliaresFile(index: number): void {
    this.familiaresFileUploadError = '';
    this.selectedFamiliaresFiles.splice(index, 1);
    this.familiaresFilePreviews.splice(index, 1);
  }

  // Método para generar preview de archivos de familiares
  private generateFamiliaresFilePreview(file: File): void {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        this.familiaresFilePreviews.push({
          url: reader.result!,
          name: file.name,
          type: file.type,
          isImage: true
        });
      };
      reader.readAsDataURL(file);
    } else {
      this.familiaresFilePreviews.push({
        url: '',
        name: file.name,
        type: file.type,
        isImage: false
      });
    }
  }

  // Método para calcular documentos requeridos para familiares (AHORA 3 POR CADA FAMILIAR)
  getMinFamiliaresRequiredFiles(): number {
    const familiaresConDoc = this.getFamiliaresQueRequierenDocumentacion();
    const totalDNIs = familiaresConDoc.length * 2;
    const totalCertificados = this.familiaresFormArray.length;
    const totalRUB = this.getFamiliaresMayoresDeEdad().length;
    const totalAlumnoRegular = this.getHijosMayoresDe21().length;
    const totalCUD = this.getFamiliaresDiscapacitados().length;

    return totalDNIs + totalCertificados + totalRUB + totalAlumnoRegular + totalCUD;
  }
  
  getFamiliaresMayoresDeEdad(): any[] {
    const mayores: any[] = [];
    
    this.familiaresFormArray.controls.forEach(familiar => {
      const fechaNacimiento = familiar.get('fechaNacimiento')?.value;
      if (fechaNacimiento && this.esMayorDeEdadFecha(fechaNacimiento)) {
        mayores.push(familiar);
      }
    });
    
    return mayores;
  }

  esMayorDeEdadFecha(fechaNacimiento: Date): boolean {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    return edad >= 18;
  }

  // Método para obtener descripción de requisitos
  getFamiliaresRequirementDescription(): string {
    if (this.familiaresFormArray.length === 0) {
      return 'No hay familiares agregados';
    }
    
    let hijosCount = 0;
    let espososCount = 0;
    let concubinosCount = 0;
    
    this.familiaresFormArray.controls.forEach(familiar => {
      const parentesco = familiar.get('parentesco')?.value;
      
      if (parentesco === Parentesco.Hijo) {
        hijosCount++;
      } else if (parentesco === Parentesco.Esposo) {
        espososCount++;
      } else if (parentesco === Parentesco.Concubino) {
        concubinosCount++;
      }
    });
    
    const partes: string[] = [];

    const familiaresConDoc = this.getFamiliaresQueRequierenDocumentacion();
    const totalDNIs = familiaresConDoc.length * 2;

    if (totalDNIs > 0) {
      partes.push(`${totalDNIs} DNI de familiar${familiaresConDoc.length !== 1 ? 'es' : ''}`);
    }
    
    if (hijosCount > 0) {
      partes.push(`${hijosCount} partida${hijosCount !== 1 ? 's' : ''} de nacimiento`);
    }
    
    if (espososCount > 0) {
      partes.push(`${espososCount} certificado${espososCount !== 1 ? 's' : ''} de matrimonio`);
    }
    
    if (concubinosCount > 0) {
      partes.push(`${concubinosCount} certificado${concubinosCount !== 1 ? 's' : ''} de concubinato`);
    }
    
    const mayoresDeEdad = this.getFamiliaresMayoresDeEdad().length;
    const hijosMayores21 = this.getHijosMayoresDe21().length;
    const discapacitados = this.getFamiliaresDiscapacitados().length;
    
    const totalCertificados = hijosCount + espososCount + concubinosCount;
    const totalArchivos = totalDNIs + totalCertificados + mayoresDeEdad + hijosMayores21 + discapacitados;
    
    if (totalArchivos === 0) {
      return 'No se requieren documentos para los familiares cargados';
    }
    
    let descripcion = `Se requieren ${totalArchivos} archivo${totalArchivos !== 1 ? 's' : ''}`;
    
    if (partes.length > 0) {
      descripcion += `: ${partes.join(', ')}`;
    }
    
    // Agregar RUB, certificados de alumno regular y CUD
    const extras: string[] = [];
    
    if (mayoresDeEdad > 0) {
      extras.push(`${mayoresDeEdad} RUB`);
    }
    
    if (hijosMayores21 > 0) {
      extras.push(`${hijosMayores21} certificado${hijosMayores21 !== 1 ? 's' : ''} de alumno regular`);
    }
    
    if (discapacitados > 0) {
      extras.push(`${discapacitados} CUD`);
    }
    
    if (extras.length > 0) {
      if (partes.length > 0) {
        descripcion += `, ${extras.join(', ')}`;
      } else {
        descripcion += `: ${extras.join(', ')}`;
      }
    }
    
    return descripcion;
  }

  // Método para validar requisitos de archivos de familiares
  validateFamiliaresFileRequirements(): boolean {
    const minRequired = this.getMinFamiliaresRequiredFiles();
    return this.selectedFamiliaresFiles.length >= minRequired;
  }

  // Método para obtener mensaje de validación de familiares
  getFamiliaresValidationMessage(): string {
    const totalRequeridos = this.getMinFamiliaresRequiredFiles();
    const archivosSubidos = this.selectedFamiliaresFiles.length;
    
    if (totalRequeridos === 0) {
      return 'No se requieren documentos para el grupo familiar';
    }
    
    if (archivosSubidos === 0) {
      return `No se han subido archivos. ${this.getFamiliaresRequirementDescription()}`;
    }
    
    if (archivosSubidos < totalRequeridos) {
      const faltan = totalRequeridos - archivosSubidos;
      return `Faltan ${faltan} archivo${faltan !== 1 ? 's' : ''}. ${this.getFamiliaresRequirementDescription()}. Archivos subidos: ${archivosSubidos}`;
    }
    
    return `Documentación del grupo familiar completa (${archivosSubidos}/${totalRequeridos} archivos)`;
  }

  // Método para obtener mensaje de requisitos de familiares
  getFamiliaresFileRequirementMessage(): string {
    const minRequired = this.getMinFamiliaresRequiredFiles();
    const currentFiles = this.selectedFamiliaresFiles.length;
    
    return `Requiere ${minRequired} archivos: ${this.getFamiliaresRequirementDescription()}. Subidos: ${currentFiles}`;
  }

  // Método para subir archivos de familiares (resiliente: acumula fallos, no aborta)
  private async uploadFamiliaresFiles(solicitudCreada: any): Promise<void> {
    await this.subirGrupoPendiente(solicitudCreada, 'familiares');
  }

  // async probarDesencriptacion() {
  //   try {
  //       console.log('🔍 Probando desencriptación...');
  //       const sede = { signoSedes: ['689e41b9bf64830c3ec6a613'] };
  //       const fecha = { 
  //           fechaDesde: new Date('2024-01-01'), 
  //           fechaHasta: new Date() 
  //       };
        
  //       const resultado = await this.solicitudesService.getSolicitudesBySedeEncrypted(sede, fecha);
  //       console.log('✅ Desencriptación funcionando:', resultado);
  //     } catch (error) {
  //         console.error('❌ Error en desencriptación:', error);
  //     }
  // }

  esMayorDeEdad(): boolean {
    const fechaNacimiento = this.familiarForm.get('fechaNacimiento')?.value;
    if (!fechaNacimiento) {
      return false;
    }
    
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    return edad >= 18;
  }

  getDniCodemRequirementDescription(): string {
    return 'Se requieren 3 archivos: 2 DNI del titular (frente y dorso) y 1 CODEM';
  }

  getDniCodemValidationMessage(): string {
    const totalRequeridos = 3;
    const archivosSubidos = this.selectedDniFiles.length;

    if (archivosSubidos === 0) {
      return `No se han subido archivos. ${this.getDniCodemRequirementDescription()}`;
    }

    if (archivosSubidos < totalRequeridos) {
      const faltan = totalRequeridos - archivosSubidos;
      return `Faltan ${faltan} archivo${faltan !== 1 ? 's' : ''}. ${this.getDniCodemRequirementDescription()}. Archivos subidos: ${archivosSubidos}`;
    }

    return `Documentación completa (${archivosSubidos}/${totalRequeridos} archivos)`;
  }

  getActividadRequirementDescription(): string {
    if (this.actividadDocumentosRequeridos.length === 0) {
      return 'No se requieren documentos adicionales';
    }
    
    const documentos = this.actividadDocumentosRequeridos.map(doc => 
      doc.documento.nombre
    ).join(', ');
    
    const total = this.actividadDocumentosRequeridos.length;
    return `Se requieren ${total} archivo${total !== 1 ? 's' : ''}: ${documentos}`;
  }

  // Método para obtener hijos mayores de 21 años
  private getHijosMayoresDe21(): any[] {
    const hijosMayores21: any[] = [];
    
    this.familiaresFormArray.controls.forEach(familiar => {
      const parentesco = familiar.get('parentesco')?.value;
      const fechaNacimiento = familiar.get('fechaNacimiento')?.value;
      
      if (parentesco === Parentesco.Hijo && fechaNacimiento && this.esMayorDe21(fechaNacimiento)) {
        hijosMayores21.push(familiar);
      }
    });
    
    return hijosMayores21;
  }

  private esMayorDe21(fechaNacimiento: Date): boolean {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    return edad >= 21;
  }

  // Método para obtener familiares discapacitados
  public getFamiliaresDiscapacitados(): any[] {
    const discapacitados: any[] = [];
    
    this.familiaresFormArray.controls.forEach(familiar => {
      const incapacidad = familiar.get('incapacidad')?.value;
      if (incapacidad === true) {
        discapacitados.push(familiar);
      }
    });
    
    return discapacitados;
  }

  // Método auxiliar para verificar usuario existente y si tiene solicitudes anuladas
  private async checkExistingSolicitudByEmail(email: string): Promise<{
    exists: boolean;
    userId?: string;
    hasAnnulledSolicitud: boolean;
  }> {
    try {
      console.log('🔍 Buscando solicitud para email:', email);
      
      const fechaDesde = new Date('2025-06-01');
      const fechaHasta = new Date();
      fechaHasta.setFullYear(fechaHasta.getFullYear() + 1);

      const sede = { signoSedes: ['689e41b9bf64830c3ec6a613'] };
      const fecha = {
        fechaDesde: fechaDesde.toISOString().split('T')[0],
        fechaHasta: fechaHasta.toISOString().split('T')[0],
      };

      console.log('Parámetros de búsqueda:', { sede, fecha, email: email.toLowerCase() });

      const solicitudes = await this.solicitudesService.getSolicitudesBySedeEncrypted(
        sede,
        fecha,
        email.toLowerCase()
      );

      console.log('📨 Solicitudes obtenidas:', solicitudes);

      if (!solicitudes || solicitudes.length === 0) {
        console.log('❌ No se encontró solicitud');
        return { exists: false, hasAnnulledSolicitud: false };
      }

      const solicitud = solicitudes[0];
      // Tomar el último estado del array "states", igual que en el banner
      const estado = solicitud.states?.[solicitud.states.length - 1]?.numberState;
      const isAnnulled = estado === 5;
      console.log('📌 Solicitud encontrada. Estado:', estado, 'Anulada:', isAnnulled);

      return { exists: true, userId: solicitud.usuario?._id, hasAnnulledSolicitud: isAnnulled };
    } catch (error) {
      console.error('❌ Error en checkExistingSolicitudByEmail:', error);
      this.inscripcionTrace.logError(AuditoriaAccion.INSCRIPCION_ERROR, error, {
        paso: 'checkExistingSolicitudByEmail (pre-chequeo)',
      });
      return { exists: false, hasAnnulledSolicitud: false };
    }
  }

  createFormDdjj () {
    this.declaracionForm = this.fb.group({
      datosPersonales: this.fb.group({
        nombre: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]],
        apellido: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]],
        fechaNacimiento: [null, Validators.required],
        dni: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
        domicilio: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        localidad: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]],
        telefono: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
        empleador: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        cuit: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
        grupoSanguineo: [null, Validators.required],
        factorRh: [null, Validators.required],
        otros: ['',],
      }),
      antecedentesMedicos: this.fb.group({
        atencionMedica: this.fb.group({
          bajoAtencion: [null, Validators.required],
          medicoAtiende: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]],
          enfermedad: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
          cualEnfermedad: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        }),
        medicamentosUltimos6Meses: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        motivoConsultaActual: [null, Validators.required],
      }),
      habitosFisiologicos: this.fb.group({
        alimentacion: this.fb.group({
          comidasDiarias: [null, Validators.required],
          queAlimentacion: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
          incluyeDesayuno: [null, Validators.required],
        }),
        sueno: this.fb.group({
          suenoTranquilo: [null, Validators.required],
          horasSueno: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj), Validators.min(0), Validators.max(24)]],
        }),
        constipacion: [null, Validators.required],
        deporte: this.fb.group({
          realizaDeporte: [null, Validators.required],
          cualDeporte: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]],
        }),
      }),
      habitosToxicos: this.fb.group({
        fumador: this.fb.group({
          fuma: [null, Validators.required],
          desdeCuando: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
          cantidadPorDia: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
        }),
        alcohol: this.fb.group({
          tomaAlcohol: [null, Validators.required],
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
          cuando: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
          cuanto: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        }),
        drogas: this.fb.group({
          consumeDrogas: [null, Validators.required],
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        }),
        automedicacion: this.fb.group({
          seAutomedica: [null, Validators.required],
          medicacion: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
          frecuencia: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        }),
      }),
      antecedentesGinecoObstetricos: this.fb.group({
        alteracionesMenstruales: this.fb.group({
          tiene: [null, Validators.required],
          primeraMenstruacionEdad: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
          ultimaMenstruacionFecha: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        }),
        embarazos: this.fb.group({
          tuvo: [null, Validators.required],
          cantidad: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
          partoNormal: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
          cesareas: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
          abortos: ['', [Validators.required, Validators.pattern(this.patronNumericoDdjj)]],
        }),
        patologiaMamaria: this.fb.group({
          tiene: [null, Validators.required],
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]],
        }),
      }),
      antecedentesFamiliares: this.fb.group({
        artritisFamiliar: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadesInmunologicas: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        cancer: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadCardiaca: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        tensionAlta: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadRenal: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        tuberculosis: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadesMentales: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadesHereditariasGeneticas: this.fb.group({ 
          tiene: [null, Validators.required], 
          quien: ['', [Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]], 
          cuales: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
      }),
      antecedentesPatologicos: this.fb.group({
        diabetes: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        hipertension: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadCardiaca: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        alergia: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        asma: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        tuberculosis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        calculosRenales: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        infeccionUrinaria: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        hepatitis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        chagas: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        brucelosis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        toxoplasmosis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        trastornosPsiquiatricos: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        trastornosMarcha: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        dispositivosMedicos: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadesTransmisionSexual: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        alteracionesEndocrinas: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        artritis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        enfermedadesPiel: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        epilepsia: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        ulceraGastrica: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        gastritis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        hemorroides: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        hernias: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        sinusitis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        cancerTumores: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        varices: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        discapacidad: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        incapacidad: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        medicoEspecialTraslado: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
      }),
      sintomasYExperiencias: this.fb.group({
        mareosDesmayos: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        convulsiones: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        doloresCabeza: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        neuralgiaNeuritis: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        depresion: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        trastornosOculares: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        supuracionOidos: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        resfriosFrecuentes: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        tosCronica: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        sudoresNocturnos: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        perdidaPeso: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        doloresPecho: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        temblores: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        debilidadMuscular: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        faltaAire: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        palpitaciones: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        acidez: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        dificultadOrinar: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        sangreOrina: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        articulacionesHinchadas: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        lumbalgia: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        ciatalgia: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        desviacionColumna: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        fracturas: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        traumatismos: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        cirugias: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        internaciones: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
        otros: this.fb.group({ 
          tiene: [null, Validators.required], 
          cual: ['', [Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]] 
        }),
      }),
      antecedentesQuirurgicos: this.fb.group({
        traumatologica: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        cardiaca: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        ginecologica: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        general: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        neuroquirurgicas: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        malformacionesNacimiento: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        hidrocefalia: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
        malformacionesGeneticas: this.fb.group({
          tiene: [null, Validators.required],
          cual: ['', [Validators.pattern(this.patronAlfanumericoDdjj)]]
        }),
      }),
    });
  }

  async onSubmitDdjj(): Promise<void> {
    if (this.isUploadingDDJJ) {
      return;
    }
    if (this.declaracionForm.valid) {
      this.showConfirmarDdjjModal = true;
    } else {
      this.declaracionForm.markAllAsTouched();

      const primeraSeccionConErrores = this.encontrarPrimeraSeccionConErrores();
      if (primeraSeccionConErrores) {
        this.scrollTo (primeraSeccionConErrores)
      }
    }
  }


  irAHome(): void {
    this.showDdjjExitosaModal = false;
    this.router.navigate(['/home']);
  }
  

  checkScreenSize() {
    this.esMovil = window.innerWidth <= 768;
  }

  toggleNav() {
    this.abrirNav = !this.abrirNav;
  }


  verificarEstadoSecciones() {
    // Lógica para verificar cada sección
    Object.keys(this.estadoSecciones).forEach(seccion => {
      this.estadoSecciones[seccion] = this.estaSeccionCompleta(seccion);
    });
  }

  estaSeccionCompleta(seccionKey: string): boolean {
    const seccion = this.declaracionForm.get(seccionKey);
    if (!seccion) return false;

    const verificarControl = (control: any, path: string = ''): boolean => {
      if (control instanceof FormGroup) {
        return Object.keys(control.controls).every(key => {
          const newPath = path ? `${path}.${key}` : key;
          // Excluir específicamente 'datosPersonales.otros'
          if (newPath === 'datosPersonales.otros') {
            return true;
          }
          return verificarControl(control.get(key), newPath);
        });
      } else {
        // Para controles normales, excluir 'otros' en 'datosPersonales'
        if (path === 'datosPersonales.otros') {
          return true;
        }
        
        // Si el control está deshabilitado, lo consideramos completo
        if (control.disabled) {
          return true;
        }
        
        // Verificar si el control tiene validadores requeridos (compatible con versiones antiguas de Angular)
        const hasRequiredValidator = this.tieneValidadorRequired(control);
        
        // Si NO tiene validador required, lo consideramos automáticamente completo
        if (!hasRequiredValidator) {
          return true;
        }
        
        // Si SÍ tiene validador required, entonces verificamos que tenga valor
        const value = control.value;
        
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (typeof value === 'number' && isNaN(value)) return false;
        if (typeof value === 'boolean' && value === null) return false;
        
        return true;
      }
    };

    return verificarControl(seccion, seccionKey);
  }

  // Función auxiliar para verificar si un control tiene el validador required
  private tieneValidadorRequired(control: any): boolean {
    if (!control.validator) {
      return false;
    }
    
    // Crear un objeto de error falso para verificar
    const validationResult = control.validator({} as any);
    
    // Si validationResult contiene 'required', entonces tiene el validador
    return validationResult && validationResult.required === true;
  }

  calcularProgreso() {
    const seccionesCompletas = Object.values(this.estadoSecciones).filter(estado => estado).length;
    const totalSecciones = Object.keys(this.estadoSecciones).length;
    this.progresoTotal = Math.round((seccionesCompletas / totalSecciones) * 100);
  }

  getErrorMessage(controlPath: string): string {
    const control = this.declaracionForm.get(controlPath);
    
    if (!control) return '';
    
    // Solo mostrar error si el control tiene validadores (no fue desactivado condicionalmente)
    // y está marcado como inválido Y tocado
    if (control.invalid && control.touched && control.errors) {
      if (control.hasError('required')) {
        return 'Este campo es obligatorio';
      }
      
      if (control.hasError('pattern')) {
        const lowerPath = controlPath.toLowerCase();
        
        if (lowerPath.includes('nombre') || lowerPath.includes('apellido') || 
            lowerPath.includes('localidad') || lowerPath.includes('medicoatiende') ||
            lowerPath.includes('quien') || lowerPath.includes('deporte')) {
          return 'Solo se permiten caracteres alfabéticos';
        } 
        else if (lowerPath.includes('dni') || lowerPath.includes('telefono') || 
                lowerPath.includes('cuit') || lowerPath.includes('cantidad') ||
                lowerPath.includes('edad') || lowerPath.includes('horas') ||
                lowerPath.includes('partonormal') || lowerPath.includes('cesareas') ||
                lowerPath.includes('abortos')) {
          return 'Solo se permiten caracteres numéricos';
        }
        else {
          return 'Solo se permiten caracteres alfanuméricos';
        }
      }
      
      if (control.hasError('min') || control.hasError('max')) {
        return 'El valor está fuera del rango permitido';
      }
    }
    
    return '';
  }


  private cargarDatosMockDDJJ(): void {
    const datosMock = {
      datosPersonales: {
        nombre: 'Juan',
        apellido: 'Pérez',
        fechaNacimiento: new Date(1990, 5, 15),
        dni: '30123456',
        domicilio: 'Av. Siempre Viva 742',
        localidad: 'Springfield',
        telefono: '1123456789',
        empleador: 'Empresa Ejemplo S.A.',
        cuit: '30123456789',
        grupoSanguineo: 'O',
        factorRh: false,
        otros: 'Ejemplo'
      },
      antecedentesMedicos: {
        atencionMedica: {
          bajoAtencion: true,
          medicoAtiende: "Ejemplo",
          enfermedad: "Ejemplo",
          cualEnfermedad: "Ejemplo"
        },
        medicamentosUltimos6Meses: "Ejemplo",
        motivoConsultaActual: true
      },
      habitosFisiologicos: {
        alimentacion: {
          comidasDiarias: true,
          queAlimentacion: "Ejemplo",
          incluyeDesayuno: true
        },
        sueno: {
          suenoTranquilo: true,
          horasSueno: "8"
        },
        constipacion: true,
        deporte: {
          realizaDeporte: true,
          cualDeporte: "Ejemplo"
        }
      },
      habitosToxicos: {
        fumador: {
          fuma: true,
          desdeCuando: "Ejemplo",
          cantidadPorDia: "1"
        },
        alcohol: {
          tomaAlcohol: true,
          cuales: "Ejemplo",
          cuando: "Ejemplo",
          cuanto: "Ejemplo"
        },
        drogas: {
          consumeDrogas: true,
          cuales: "Ejemplo"
        },
        automedicacion: {
          seAutomedica: true,
          medicacion: "Ejemplo",
          frecuencia: "Ejemplo"
        }
      },
      antecedentesGinecoObstetricos: {
        alteracionesMenstruales: {
          tiene: true,
          primeraMenstruacionEdad: "1",
          ultimaMenstruacionFecha: "1"
        },
        embarazos: {
          tuvo: false,
          cantidad: "0",
          partoNormal: "0",
          cesareas: "0",
          abortos: "0"
        },
        patologiaMamaria: {
          tiene: true,
          cuales: "Ejemplo"
        }
      },
      antecedentesFamiliares: {
        artritisFamiliar: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        enfermedadesInmunologicas: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        cancer: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        enfermedadCardiaca: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        tensionAlta: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        enfermedadRenal: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        tuberculosis: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        enfermedadesMentales: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        },
        enfermedadesHereditariasGeneticas: {
          tiene: true,
          quien: "Ejemplo",
          cuales: "Ejemplo"
        }
      },
      antecedentesPatologicos: {
        diabetes: {
          tiene: true,
          cual: "Ejemplo"
        },
        hipertension: {
          tiene: true,
          cual: "Ejemplo"
        },
        enfermedadCardiaca: {
          tiene: true,
          cual: "Ejemplo"
        },
        alergia: {
          tiene: true,
          cual: "Ejemplo"
        },
        asma: {
          tiene: true,
          cual: "Ejemplo"
        },
        tuberculosis: {
          tiene: true,
          cual: "Ejemplo"
        },
        calculosRenales: {
          tiene: true,
          cual: "Ejemplo"
        },
        infeccionUrinaria: {
          tiene: true,
          cual: "Ejemplo"
        },
        hepatitis: {
          tiene: true,
          cual: "Ejemplo"
        },
        chagas: {
          tiene: true,
          cual: "Ejemplo"
        },
        brucelosis: {
          tiene: true,
          cual: "Ejemplo"
        },
        toxoplasmosis: {
          tiene: true,
          cual: "Ejemplo"
        },
        trastornosPsiquiatricos: {
          tiene: true,
          cual: "Ejemplo"
        },
        trastornosMarcha: {
          tiene: true,
          cual: "Ejemplo"
        },
        dispositivosMedicos: {
          tiene: true,
          cual: "Ejemplo"
        },
        enfermedadesTransmisionSexual: {
          tiene: true,
          cual: "Ejemplo"
        },
        alteracionesEndocrinas: {
          tiene: true,
          cual: "Ejemplo"
        },
        artritis: {
          tiene: true,
          cual: "Ejemplo"
        },
        enfermedadesPiel: {
          tiene: true,
          cual: "Ejemplo"
        },
        epilepsia: {
          tiene: true,
          cual: "Ejemplo"
        },
        ulceraGastrica: {
          tiene: true,
          cual: "Ejemplo"
        },
        gastritis: {
          tiene: true,
          cual: "Ejemplo"
        },
        hemorroides: {
          tiene: true,
          cual: "Ejemplo"
        },
        hernias: {
          tiene: true,
          cual: "Ejemplo"
        },
        sinusitis: {
          tiene: true,
          cual: "Ejemplo"
        },
        cancerTumores: {
          tiene: true,
          cual: "Ejemplo"
        },
        varices: {
          tiene: true,
          cual: "Ejemplo"
        },
        discapacidad: {
          tiene: true,
          cual: "Ejemplo"
        },
        incapacidad: {
          tiene: true,
          cual: "Ejemplo"
        },
        medicoEspecialTraslado: {
          tiene: true,
          cual: "Ejemplo"
        }
      },
      sintomasYExperiencias: {
        mareosDesmayos: {
          tiene: true,
          cual: "Ejemplo"
        },
        convulsiones: {
          tiene: true,
          cual: "Ejemplo"
        },
        doloresCabeza: {
          tiene: true,
          cual: "Ejemplo"
        },
        neuralgiaNeuritis: {
          tiene: true,
          cual: "Ejemplo"
        },
        depresion: {
          tiene: true,
          cual: "Ejemplo"
        },
        trastornosOculares: {
          tiene: true,
          cual: "Ejemplo"
        },
        supuracionOidos: {
          tiene: true,
          cual: "Ejemplo"
        },
        resfriosFrecuentes: {
          tiene: true,
          cual: "Ejemplo"
        },
        tosCronica: {
          tiene: true,
          cual: "Ejemplo"
        },
        sudoresNocturnos: {
          tiene: true,
          cual: "Ejemplo"
        },
        perdidaPeso: {
          tiene: true,
          cual: "Ejemplo"
        },
        doloresPecho: {
          tiene: true,
          cual: "Ejemplo"
        },
        temblores: {
          tiene: true,
          cual: "Ejemplo"
        },
        debilidadMuscular: {
          tiene: true,
          cual: "Ejemplo"
        },
        faltaAire: {
          tiene: true,
          cual: "Ejemplo"
        },
        palpitaciones: {
          tiene: true,
          cual: "Ejemplo"
        },
        acidez: {
          tiene: true,
          cual: "Ejemplo"
        },
        dificultadOrinar: {
          tiene: true,
          cual: "Ejemplo"
        },
        sangreOrina: {
          tiene: true,
          cual: "Ejemplo"
        },
        articulacionesHinchadas: {
          tiene: true,
          cual: "Ejemplo"
        },
        lumbalgia: {
          tiene: true,
          cual: "Ejemplo"
        },
        ciatalgia: {
          tiene: true,
          cual: "Ejemplo"
        },
        desviacionColumna: {
          tiene: true,
          cual: "Ejemplo"
        },
        fracturas: {
          tiene: true,
          cual: "Ejemplo"
        },
        traumatismos: {
          tiene: true,
          cual: "Ejemplo"
        },
        cirugias: {
          tiene: true,
          cual: "Ejemplo"
        },
        internaciones: {
          tiene: true,
          cual: "Ejemplo"
        },
        otros: {
          tiene: true,
          cual: "Ejemplo"
        }
      },
      antecedentesQuirurgicos: {
        traumatologica: { tiene: true, cual: 'Ejemplo' },
        cardiaca: { tiene: true, cual: 'Ejemplo' },
        ginecologica: { tiene: true, cual: 'Ejemplo' },
        general: { tiene: true, cual: 'Ejemplo' },
        neuroquirurgicas: { tiene: true, cual: 'Ejemplo' },
        malformacionesNacimiento: { tiene: true, cual: 'Ejemplo' },
        hidrocefalia: { tiene: true, cual: 'Ejemplo' },
        malformacionesGeneticas: { tiene: true, cual: 'Ejemplo' }
      }
    };

    this.declaracionForm.patchValue(datosMock);
    console.log('MODO ADMIN: Datos de prueba cargados');
    this.verificarEstadoSecciones();
    this.calcularProgreso();
  }


  private encontrarPrimeraSeccionConErrores(): string | null {
    const mapeoSecciones = {
      'datosPersonales': 'datos-personales',
      'antecedentesMedicos': 'antecedentes-medicos', 
      'habitosFisiologicos': 'habitos-fisiologicos',
      'habitosToxicos': 'habitos-toxicos',
      'antecedentesGinecoObstetricos': 'antecedentes-gineco-obstetricos',
      'antecedentesFamiliares': 'antecedentes-familiares',
      'antecedentesPatologicos': 'antecedentes-patologicos',
      'sintomasYExperiencias': 'sintomas-y-experiencias',
      'antecedentesQuirurgicos': 'antecedentes-quirurgicos'
    };

    // Buscar en orden la primera sección que tenga controles inválidos
    for (const [seccionKey, seccionId] of Object.entries(mapeoSecciones)) {
      const seccion = this.declaracionForm.get(seccionKey);
      if (seccion && seccion.invalid) {
        return seccionId;
      }
    }

    return null;
  }


  //// NUEVAS FUNCIONES DE VALIDACION CONDICIONAL

  private setupAllConditionalValidations(): void {

    this.setupAtencionMedicaValidation();
    this.setupDeporteValidation();

    //// Hábitos Tóxicos
    this.setupFumadorValidation();
    this.setupAlcoholValidation();
    this.setupDrogasValidation();
    this.setupAutomedicacionValidation();

    //// Antecedentes Gineco-Obstétricos
    this.setupAlteracionesMenstrualesValidation();
    this.setupEmbarazosValidation();
    this.setupPatologiaMamariaValidation();

    //// Antecedentes Familiares (todas las enfermedades)
    this.setupFamiliaresValidation('artritisFamiliar');
    this.setupFamiliaresValidation('enfermedadesInmunologicas');
    this.setupFamiliaresValidation('cancer');
    this.setupFamiliaresValidation('enfermedadCardiaca');
    this.setupFamiliaresValidation('tensionAlta');
    this.setupFamiliaresValidation('enfermedadRenal');
    this.setupFamiliaresValidation('tuberculosis');
    this.setupFamiliaresValidation('enfermedadesMentales');
    this.setupFamiliaresValidation('enfermedadesHereditariasGeneticas');

    ////
    this.setupAntecedentesQuirurgicosValidation();

    //// Antecedentes Patológicos (todas las enfermedades)
    const enfermedadesPatologicas = [
      'diabetes', 'hipertension', 'enfermedadCardiaca', 'alergia', 'asma',
      'tuberculosis', 'calculosRenales', 'infeccionUrinaria', 'hepatitis',
      'chagas', 'brucelosis', 'toxoplasmosis', 'trastornosPsiquiatricos',
      'trastornosMarcha', 'dispositivosMedicos', 'enfermedadesTransmisionSexual',
      'alteracionesEndocrinas', 'artritis', 'enfermedadesPiel', 'epilepsia',
      'ulceraGastrica', 'gastritis', 'hemorroides', 'hernias', 'sinusitis',
      'cancerTumores', 'varices', 'discapacidad', 'incapacidad', 'medicoEspecialTraslado'
    ];
    
    enfermedadesPatologicas.forEach(enfermedad => {
      this.setupPatologicosValidation(enfermedad);
    });

    //// Síntomas y Experiencias (todos los síntomas)
    const sintomas = [
      'mareosDesmayos', 'convulsiones', 'doloresCabeza', 'neuralgiaNeuritis',
      'depresion', 'trastornosOculares', 'supuracionOidos', 'resfriosFrecuentes',
      'tosCronica', 'sudoresNocturnos', 'perdidaPeso', 'doloresPecho', 'temblores',
      'debilidadMuscular', 'faltaAire', 'palpitaciones', 'acidez', 'dificultadOrinar',
      'sangreOrina', 'articulacionesHinchadas', 'lumbalgia', 'ciatalgia',
      'desviacionColumna', 'fracturas', 'traumatismos', 'cirugias', 'internaciones', 'otros'
    ];
    
    sintomas.forEach(sintoma => {
      this.setupSintomasValidation(sintoma);
    });
  }

  //// VALIDACION FUMADOR
  
  private setupFumadorValidation(): void {
    const fumadorGroup = this.declaracionForm.get('habitosToxicos.fumador');
    if (!fumadorGroup) return;

    const fumaControl = fumadorGroup.get('fuma');
    const desdeCuandoControl = fumadorGroup.get('desdeCuando');
    const cantidadPorDiaControl = fumadorGroup.get('cantidadPorDia');

    if (fumaControl && desdeCuandoControl && cantidadPorDiaControl) {
      fumaControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          // Si es TRUE → aplicar validadores
          desdeCuandoControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cantidadPorDiaControl.setValidators([Validators.required, Validators.pattern(this.patronNumericoDdjj)]);
        } else {
          // Si es FALSE o NULL → quitar validadores y setear valor VACÍO
          desdeCuandoControl.clearValidators();
          cantidadPorDiaControl.clearValidators();
          desdeCuandoControl.setValue('', { emitEvent: false });  // CAMBIADO: '' en lugar de '0'
          cantidadPorDiaControl.setValue('', { emitEvent: false });  // CAMBIADO: '' en lugar de '0'
          desdeCuandoControl.markAsPristine();
          cantidadPorDiaControl.markAsPristine();
        }
        desdeCuandoControl.updateValueAndValidity({ emitEvent: false });
        cantidadPorDiaControl.updateValueAndValidity({ emitEvent: false });
      });

      // Estado inicial - también debe ser vacío si no fuma
      if (fumaControl.value !== true) {
        desdeCuandoControl.setValue('', { emitEvent: false });
        cantidadPorDiaControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION ALCOHOL

  private setupAlcoholValidation(): void {
    const alcoholGroup = this.declaracionForm.get('habitosToxicos.alcohol');
    if (!alcoholGroup) return;

    const tomaAlcoholControl = alcoholGroup.get('tomaAlcohol');
    const cualesControl = alcoholGroup.get('cuales');
    const cuandoControl = alcoholGroup.get('cuando');
    const cuantoControl = alcoholGroup.get('cuanto');

    if (tomaAlcoholControl && cualesControl && cuandoControl && cuantoControl) {
      tomaAlcoholControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          // Si es TRUE → aplicar validadores
          cualesControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cuandoControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cuantoControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cualesControl.markAsTouched();
          cuandoControl.markAsTouched();
          cuantoControl.markAsTouched();
        } else {
          // Si es FALSE o NULL → quitar validadores y setear valor por defecto
          cualesControl.clearValidators();
          cuandoControl.clearValidators();
          cuantoControl.clearValidators();
          cualesControl.setValue('', { emitEvent: false });
          cuandoControl.setValue('', { emitEvent: false });
          cuantoControl.setValue('', { emitEvent: false });
          cualesControl.markAsPristine();
          cuandoControl.markAsPristine();
          cuantoControl.markAsPristine();
        }
        cualesControl.updateValueAndValidity({ emitEvent: false });
        cuandoControl.updateValueAndValidity({ emitEvent: false });
        cuantoControl.updateValueAndValidity({ emitEvent: false });
      });

      // Estado inicial
      if (tomaAlcoholControl.value !== true) {
        cualesControl.setValue('', { emitEvent: false });
        cuandoControl.setValue('', { emitEvent: false });
        cuantoControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION DROGAS
  
  private setupDrogasValidation(): void {
    const drogasGroup = this.declaracionForm.get('habitosToxicos.drogas');
    if (!drogasGroup) return;

    const consumeDrogasControl = drogasGroup.get('consumeDrogas');
    const cualesControl = drogasGroup.get('cuales');

    if (consumeDrogasControl && cualesControl) {
      consumeDrogasControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          // Si es TRUE → aplicar validadores
          cualesControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cualesControl.markAsTouched();
        } else {
          // Si es FALSE o NULL → quitar validadores y setear valor por defecto
          cualesControl.clearValidators();
          cualesControl.setValue('', { emitEvent: false });
          cualesControl.markAsPristine();
        }
        cualesControl.updateValueAndValidity({ emitEvent: false });
      });

      // Estado inicial
      if (consumeDrogasControl.value !== true) {
        cualesControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION AUTOMEDICACION

  private setupAutomedicacionValidation(): void {
    const automedicacionGroup = this.declaracionForm.get('habitosToxicos.automedicacion');
    if (!automedicacionGroup) return;

    const seAutomedicaControl = automedicacionGroup.get('seAutomedica');
    const medicacionControl = automedicacionGroup.get('medicacion');
    const frecuenciaControl = automedicacionGroup.get('frecuencia');

    if (seAutomedicaControl && medicacionControl && frecuenciaControl) {
      seAutomedicaControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          // Si es TRUE → aplicar validadores
          medicacionControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          frecuenciaControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          medicacionControl.markAsTouched();
          frecuenciaControl.markAsTouched();
        } else {
          // Si es FALSE o NULL → quitar validadores y setear valor por defecto
          medicacionControl.clearValidators();
          frecuenciaControl.clearValidators();
          medicacionControl.setValue('', { emitEvent: false });
          frecuenciaControl.setValue('', { emitEvent: false });
          medicacionControl.markAsPristine();
          frecuenciaControl.markAsPristine();
        }
        medicacionControl.updateValueAndValidity({ emitEvent: false });
        frecuenciaControl.updateValueAndValidity({ emitEvent: false });
      });

      // Estado inicial
      if (seAutomedicaControl.value !== true) {
        medicacionControl.setValue('', { emitEvent: false });
        frecuenciaControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION ALTERACIONES MENSTRUALES

  private setupAlteracionesMenstrualesValidation(): void {
    const grupo = this.declaracionForm.get('antecedentesGinecoObstetricos.alteracionesMenstruales');
    if (!grupo) return;

    const tieneControl = grupo.get('tiene');
    const primeraEdadControl = grupo.get('primeraMenstruacionEdad');
    const ultimaFechaControl = grupo.get('ultimaMenstruacionFecha');

    if (tieneControl && primeraEdadControl && ultimaFechaControl) {
      tieneControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          primeraEdadControl.setValidators([Validators.required, Validators.pattern(this.patronNumericoDdjj)]);
          ultimaFechaControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          primeraEdadControl.markAsTouched();
          ultimaFechaControl.markAsTouched();
        } else {
          primeraEdadControl.clearValidators();
          ultimaFechaControl.clearValidators();
          primeraEdadControl.setValue('', { emitEvent: false });
          ultimaFechaControl.setValue('', { emitEvent: false });
          primeraEdadControl.markAsPristine();
          ultimaFechaControl.markAsPristine();
        }
        primeraEdadControl.updateValueAndValidity({ emitEvent: false });
        ultimaFechaControl.updateValueAndValidity({ emitEvent: false });
      });

      if (tieneControl.value !== true) {
        primeraEdadControl.setValue('', { emitEvent: false });
        ultimaFechaControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION EMBARAZOS

  private setupEmbarazosValidation(): void {
    const grupo = this.declaracionForm.get('antecedentesGinecoObstetricos.embarazos');
    if (!grupo) return;

    const tuvoControl = grupo.get('tuvo');
    const cantidadControl = grupo.get('cantidad');
    const partoNormalControl = grupo.get('partoNormal');
    const cesareasControl = grupo.get('cesareas');
    const abortosControl = grupo.get('abortos');

    if (tuvoControl && cantidadControl && partoNormalControl && cesareasControl && abortosControl) {
      tuvoControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          cantidadControl.setValidators([Validators.required, Validators.pattern(this.patronNumericoDdjj)]);
          partoNormalControl.setValidators([Validators.required, Validators.pattern(this.patronNumericoDdjj)]);
          cesareasControl.setValidators([Validators.required, Validators.pattern(this.patronNumericoDdjj)]);
          abortosControl.setValidators([Validators.required, Validators.pattern(this.patronNumericoDdjj)]);
          
          [cantidadControl, partoNormalControl, cesareasControl, abortosControl].forEach(control => {
            control.markAsTouched();
          });
        } else {
          [cantidadControl, partoNormalControl, cesareasControl, abortosControl].forEach(control => {
            control.clearValidators();
            control.setValue('', { emitEvent: false });  // CAMBIADO: '' en lugar de '0'
            control.markAsPristine();
          });
        }
        
        [cantidadControl, partoNormalControl, cesareasControl, abortosControl].forEach(control => {
          control.updateValueAndValidity({ emitEvent: false });
        });
      });

      // Estado inicial también debe ser vacío
      if (tuvoControl.value !== true) {
        [cantidadControl, partoNormalControl, cesareasControl, abortosControl].forEach(control => {
          control.setValue('', { emitEvent: false });  // CAMBIADO: '' en lugar de '0'
        });
      }
    }
  }

  //// VALIDACION PATOLOGIAS MAMARIAS

  private setupPatologiaMamariaValidation(): void {
    const grupo = this.declaracionForm.get('antecedentesGinecoObstetricos.patologiaMamaria');
    if (!grupo) return;

    const tieneControl = grupo.get('tiene');
    const cualesControl = grupo.get('cuales');

    if (tieneControl && cualesControl) {
      tieneControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          cualesControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cualesControl.markAsTouched();
        } else {
          cualesControl.clearValidators();
          cualesControl.setValue('', { emitEvent: false });
          cualesControl.markAsPristine();
        }
        cualesControl.updateValueAndValidity({ emitEvent: false });
      });

      if (tieneControl.value !== true) {
        cualesControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION ANTECEDENTES FAMILIARES

  private setupFamiliaresValidation(enfermedadKey: string): void {
    const grupo = this.declaracionForm.get(`antecedentesFamiliares.${enfermedadKey}`);
    if (!grupo) return;

    const tieneControl = grupo.get('tiene');
    const quienControl = grupo.get('quien');
    const cualesControl = grupo.get('cuales');

    if (tieneControl && quienControl && cualesControl) {
      tieneControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          quienControl.setValidators([Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]);
          cualesControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          quienControl.markAsTouched();
          cualesControl.markAsTouched();
        } else {
          quienControl.clearValidators();
          cualesControl.clearValidators();
          quienControl.setValue('', { emitEvent: false });
          cualesControl.setValue('', { emitEvent: false });
          quienControl.markAsPristine();
          cualesControl.markAsPristine();
        }
        quienControl.updateValueAndValidity({ emitEvent: false });
        cualesControl.updateValueAndValidity({ emitEvent: false });
      });

      if (tieneControl.value !== true) {
        quienControl.setValue('', { emitEvent: false });
        cualesControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION ANTECEDENTES PATOLOGICOS
  
  private setupPatologicosValidation(enfermedadKey: string): void {
    const grupo = this.declaracionForm.get(`antecedentesPatologicos.${enfermedadKey}`);
    if (!grupo) return;

    const tieneControl = grupo.get('tiene');
    const cualControl = grupo.get('cual');

    if (tieneControl && cualControl) {
      tieneControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          cualControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cualControl.markAsTouched();
        } else {
          cualControl.clearValidators();
          cualControl.setValue('', { emitEvent: false });
          cualControl.markAsPristine();
        }
        cualControl.updateValueAndValidity({ emitEvent: false });
      });

      if (tieneControl.value !== true) {
        cualControl.setValue('', { emitEvent: false });
      }
    }
  }

  //// VALIDACION SINTOMAS Y EXP

  private setupSintomasValidation(sintomaKey: string): void {
    const grupo = this.declaracionForm.get(`sintomasYExperiencias.${sintomaKey}`);
    if (!grupo) return;

    const tieneControl = grupo.get('tiene');
    const cualControl = grupo.get('cual');

    if (tieneControl && cualControl) {
      tieneControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          cualControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cualControl.markAsTouched();
        } else {
          cualControl.clearValidators();
          cualControl.setValue('', { emitEvent: false });
          cualControl.markAsPristine();
        }
        cualControl.updateValueAndValidity({ emitEvent: false });
      });

      if (tieneControl.value !== true) {
        cualControl.setValue('', { emitEvent: false });
      }
    }
  }

  private setupAtencionMedicaValidation(): void {
    const atencionMedicaGroup = this.declaracionForm.get('antecedentesMedicos.atencionMedica');
    if (!atencionMedicaGroup) return;

    const bajoAtencionControl = atencionMedicaGroup.get('bajoAtencion');
    const medicoAtiendeControl = atencionMedicaGroup.get('medicoAtiende');
    const enfermedadControl = atencionMedicaGroup.get('enfermedad');
    const cualEnfermedadControl = atencionMedicaGroup.get('cualEnfermedad');

    const medicamentosControl = this.declaracionForm.get('antecedentesMedicos.medicamentosUltimos6Meses');

    if (bajoAtencionControl && medicoAtiendeControl && enfermedadControl && cualEnfermedadControl && medicamentosControl) {
      bajoAtencionControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          // Si está bajo atención → aplicar validadores
          medicoAtiendeControl.setValidators([Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]);
          enfermedadControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          cualEnfermedadControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
          medicamentosControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
        } else {
          // Si NO está bajo atención → quitar validadores y limpiar
          medicoAtiendeControl.clearValidators();
          enfermedadControl.clearValidators();
          cualEnfermedadControl.clearValidators();
          medicamentosControl.clearValidators();
          
          medicoAtiendeControl.setValue('', { emitEvent: false });
          enfermedadControl.setValue('', { emitEvent: false });
          cualEnfermedadControl.setValue('', { emitEvent: false });
          medicamentosControl.setValue('', { emitEvent: false });
          
          medicoAtiendeControl.markAsPristine();
          enfermedadControl.markAsPristine();
          cualEnfermedadControl.markAsPristine();
          medicamentosControl.markAsPristine();
        }
        medicoAtiendeControl.updateValueAndValidity({ emitEvent: false });
        enfermedadControl.updateValueAndValidity({ emitEvent: false });
        cualEnfermedadControl.updateValueAndValidity({ emitEvent: false });
        medicamentosControl.updateValueAndValidity({ emitEvent: false });
      });

      // Estado inicial
      if (bajoAtencionControl.value !== true) {
        medicoAtiendeControl.setValue('', { emitEvent: false });
        enfermedadControl.setValue('', { emitEvent: false });
        cualEnfermedadControl.setValue('', { emitEvent: false });
        medicamentosControl.setValue('', { emitEvent: false });
      }
    }
  }

  // Agregar esta función para validación condicional de deporte:
  private setupDeporteValidation(): void {
    const deporteGroup = this.declaracionForm.get('habitosFisiologicos.deporte');
    if (!deporteGroup) return;

    const realizaDeporteControl = deporteGroup.get('realizaDeporte');
    const cualDeporteControl = deporteGroup.get('cualDeporte');

    if (realizaDeporteControl && cualDeporteControl) {
      realizaDeporteControl.valueChanges.subscribe((value: boolean | null) => {
        if (value === true) {
          cualDeporteControl.setValidators([Validators.required, Validators.pattern(this.patronAlfabeticoDdjj)]);
        } else {
          cualDeporteControl.clearValidators();
          cualDeporteControl.setValue('', { emitEvent: false });
          cualDeporteControl.markAsPristine();
        }
        cualDeporteControl.updateValueAndValidity({ emitEvent: false });
      });

      // Estado inicial
      if (realizaDeporteControl.value !== true) {
        cualDeporteControl.setValue('', { emitEvent: false });
      }
    }
  }

  private setupAntecedentesQuirurgicosValidation(): void {
    const cirugias = [
      'traumatologica',
      'cardiaca',
      'ginecologica',
      'general',
      'neuroquirurgicas',
      'malformacionesNacimiento',
      'hidrocefalia',
      'malformacionesGeneticas'
    ];

    cirugias.forEach(cirugiaKey => {
      const grupo = this.declaracionForm.get(`antecedentesQuirurgicos.${cirugiaKey}`);
      if (!grupo) return;

      const tieneControl = grupo.get('tiene');
      const cualControl = grupo.get('cual');

      if (tieneControl && cualControl) {
        tieneControl.valueChanges.subscribe((value: boolean | null) => {
          if (value === true) {
            cualControl.setValidators([Validators.required, Validators.pattern(this.patronAlfanumericoDdjj)]);
            cualControl.markAsTouched();
          } else {
            cualControl.clearValidators();
            cualControl.setValue('', { emitEvent: false });
            cualControl.markAsPristine();
          }
          cualControl.updateValueAndValidity({ emitEvent: false });
        });

        // Estado inicial
        if (tieneControl.value !== true) {
          cualControl.setValue('', { emitEvent: false });
        }
      }
    });
  }

  /* ===== WIZARD METHODS ===== */

  nextStepDdjj(): void {
    if (this.stepValidoDdjj(this.ddjjStep) && this.ddjjStep < this.ddjjSteps.length) {
      this.ddjjStepErrors[this.ddjjStep - 1] = false;
      this.ddjjStep++;
      if (this.ddjjStep > this.ddjjMaxStep) {
        this.ddjjMaxStep = this.ddjjStep;
      }
      this.onStepEnterDdjj(this.ddjjStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Revelar los campos que faltan en el paso actual (los mat-error requieren touched).
      this.ddjjStepErrors[this.ddjjStep - 1] = true;
      this.revelarErroresPasoDdjj(this.ddjjStep);
      this.markStepInvalidDdjj();
    }
  }

  prevStepDdjj(): void {
    if (this.ddjjStep > 1) {
      this.ddjjStep--;
    }
  }

  irAPasoDdjj(step: number): void {
    if (step <= this.ddjjMaxStep) {
      this.ddjjStep = step;
    }
  }

  private onStepEnterDdjj(step: number): void {
    // nothing special to build per step for ddjj
  }

  stepValidoDdjj(step: number): boolean {
    switch (step) {
      case 1: return this.declaracionForm.get('datosPersonales')?.valid!;
      case 2: return this.declaracionForm.get('antecedentesMedicos')?.valid!;
      case 3: return this.declaracionForm.get('habitosFisiologicos')?.valid!;
      case 4: return this.declaracionForm.get('habitosToxicos')?.valid!;
      case 5: return this.declaracionForm.get('antecedentesGinecoObstetricos')?.valid!;
      case 6: return this.declaracionForm.get('antecedentesFamiliares')?.valid!;
      case 7: return this.declaracionForm.get('antecedentesPatologicos')?.valid!;
      case 8: return this.declaracionForm.get('sintomasYExperiencias')?.valid!;
      case 9: return this.declaracionForm.get('antecedentesQuirurgicos')?.valid!;
      default: return true;
    }
  }

  private marcarGrupoTocado(control: any): void {
    control.markAsTouched();
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.keys(control.controls).forEach(key => this.marcarGrupoTocado(control.controls[key]));
    }
  }

  private revelarErroresPasoDdjj(step: number): void {
    const rutas: { [key: number]: string } = {
      1: 'datosPersonales',
      2: 'antecedentesMedicos',
      3: 'habitosFisiologicos',
      4: 'habitosToxicos',
      5: 'antecedentesGinecoObstetricos',
      6: 'antecedentesFamiliares',
      7: 'antecedentesPatologicos',
      8: 'sintomasYExperiencias',
      9: 'antecedentesQuirurgicos',
    };
    const ruta = rutas[step];
    if (!ruta) {
      return;
    }
    const grupo = this.declaracionForm.get(ruta);
    if (grupo) {
      this.marcarGrupoTocado(grupo);
    }
  }

  private markStepInvalidDdjj(): void {
    this.scrollToTopOfWizard();
  }

  private scrollToTopOfWizard(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  //// prueba merge



  async procesarDdjj(): Promise<void> {
    // Single-flight: doble click en "Confirmar y Enviar" no re-entra.
    if (this.isUploadingDDJJ) {
      return;
    }
    this.showConfirmarDdjjModal = false;
    this.isUploadingDDJJ = true;

    const declaracion: DeclaracionJurada = this.declaracionForm.value;

    try {
      if (!this.solicitudId) {
        console.error('No se encontró el ID de la solicitud existente');
        this.isUploadingDDJJ = false;
        this.mostrarErrorDdjj(
          'No se pudo recuperar su inscripción para asociar la declaración jurada. Vuelva a intentarlo; si el problema continúa, recargue la página.'
        );
        return;
      }

      console.log('Actualizando solicitud existente con ID:', this.solicitudId);
      this.inscripcionTrace.setSolicitudId(this.solicitudId);
      const inicioDdjj = Date.now();

      const solicitudExistente = await this.solicitudesService.getSolicitudByIdEncrypted(this.solicitudId);
      console.log('Solicitud existente obtenida:', solicitudExistente);

      const regimen = this.inscripcionData?.datosDelTitular?.regimenLaboral;

      const estadosActuales = Array.isArray(solicitudExistente.states) ? solicitudExistente.states : [];
      const ultimoNumero =
        solicitudExistente.lastState?.numberState ??
        (estadosActuales.length ? estadosActuales[estadosActuales.length - 1]?.numberState : null);
      // Idempotencia: si la solicitud ya quedó en 15 (reintento), no se vuelve a duplicar el estado.
      const yaEnviada = ultimoNumero === 15;
      const nuevoEstado = {
        date: new Date().toISOString(),
        notificar: false,
        numberState: 15,
      };

      const solicitudUpdate = {
        ...solicitudExistente,
        lastState: {
          ...(solicitudExistente.lastState || {}),
          ...nuevoEstado,
        },
        states: yaEnviada ? estadosActuales : [...estadosActuales, nuevoEstado],
        ddjj: {
          contenido: declaracion,
          acepta: true,
          timeMark: new Date().toISOString(),
        },
        pais: solicitudExistente.pais || "609bd53ef04ad82d91ca2a2e",
        actividad: solicitudExistente.actividad || this.obtenerActividadIdPorRegimen(regimen),
        usuario: solicitudExistente.usuario || this.solicitudId,
        titular: solicitudExistente.titular || this.solicitudId,
        beneficiario: solicitudExistente.beneficiario || this.solicitudId,
        signoSede: solicitudExistente.signoSede || '689e41b9bf64830c3ec6a613',
      } as any;

      const solicitudActualizada = await this.solicitudesService.updateSolicitudDDJJEncrypted(this.solicitudId, solicitudUpdate);
      console.log('Solicitud actualizada exitosamente:', solicitudActualizada);

      // Verificación real: no se da por enviada si el servidor no la guardó.
      const ddjjGuardada = (solicitudActualizada as any)?.ddjj?.acepta;
      if (!solicitudActualizada || ddjjGuardada !== true) {
        throw new Error('El servidor no confirmó el guardado de la declaración jurada');
      }

      await this.storageService.removeInscripcionOsData();
      console.log('Storage limpiado exitosamente');

      this.inscripcionTrace.logStep(AuditoriaAccion.DDJJ_OK, {
        paso: 'DDJJ enviada (estado 15)',
        duracionMs: Date.now() - inicioDdjj,
        payload: { solicitudId: this.solicitudId, yaEnviada },
      });

      this.isUploadingDDJJ = false;

      this.showDdjjExitosaModal = true;
    } catch (error) {
      console.error('Error al actualizar la solicitud:', error);
      this.inscripcionTrace.logError(AuditoriaAccion.DDJJ_ERROR, error, { paso: 'procesarDdjj' });
      this.isUploadingDDJJ = false;
      this.mostrarErrorDdjj(
        'No se pudo enviar la declaración jurada en este momento. Verifique su conexión e intente nuevamente; sus datos no se perdieron.'
      );
    }
  }

  private prefillDatosPersonalesDdjj(inscripcion: any): void {
    if (!inscripcion) {
      return;
    }

    const datosTitular = inscripcion.datosDelTitular;
    if (!datosTitular) {
      return;
    }

    this.esMonotributo = datosTitular.regimenLaboral === RegimenLaboral.Monotributo;

    const domicilioTitular = datosTitular.domicilio;
    const datosEmpresa = inscripcion.datosDeLaEmpresa;

    const empleadorControl = this.declaracionForm.get('datosPersonales.empleador');
    const cuitControl = this.declaracionForm.get('datosPersonales.cuit');

    if (this.esMonotributo) {
      empleadorControl?.clearValidators();
      cuitControl?.clearValidators();
    }

    this.declaracionForm.patchValue({
      datosPersonales: {
        nombre: datosTitular.nombre,
        apellido: datosTitular.apellido,
        fechaNacimiento: datosTitular.fechaNacimiento,
        dni: datosTitular.numeroDocumento,
        domicilio: domicilioTitular ? `${domicilioTitular.direccion} ${domicilioTitular.numero}` : '',
        localidad: domicilioTitular?.localidad,
        telefono: datosTitular.telefono,
        empleador: this.esMonotributo ? '' : (datosEmpresa?.razonSocial || ''),
        cuit: this.esMonotributo ? '' : (datosEmpresa?.cuit || ''),
      },
    });

    if (this.esMonotributo) {
      empleadorControl?.updateValueAndValidity();
      cuitControl?.updateValueAndValidity();
    }

    console.log('Datos de inscripción cargados en el formulario');
  }

  async cargarDatosResumeDdjj(): Promise<void> {
    try {
      const datosExistentes = await this.storageService.getInscripcionOsData();

      if (datosExistentes && datosExistentes.data && datosExistentes.data.solicitudId) {
        this.solicitudId = datosExistentes.data.solicitudId;
        this.solicitudPendienteId = datosExistentes.data.solicitudId;
        this.inscripcionTrace.setSolicitudId(this.solicitudId);
        this.inscripcionData = datosExistentes.data.inscripcion;
        this.prefillDatosPersonalesDdjj(this.inscripcionData);
        this.avisoPaso1Completado = true;
      } else {
        // Llegó con ?phase=ddjj pero sin datos de la costura: volver a la
        // inscripción en vez de renderizar una DDJJ vacía que no se puede enviar.
        console.warn('No hay inscripción pendiente para reanudar la DDJJ');
        this.phase = 'inscripcion';
        await this.router.navigate([], {
          queryParams: { phase: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    } catch (error) {
      console.error('Error al cargar datos de inscripción:', error);
      this.inscripcionTrace.logError(AuditoriaAccion.INSCRIPCION_ERROR, error, {
        paso: 'cargarDatosResumeDdjj',
      });
      this.phase = 'inscripcion';
    }
  }


  ngOnDestroy(): void {
    this.avanceSub?.unsubscribe();
    this.limpiarAvisoSubida();
    [this.dniFileSlots, this.actividadFileSlots, this.familiaresFileSlots].forEach((slots) => {
      slots.forEach((slot) => {
        if (slot.preview?.isImage && slot.preview.url) {
          URL.revokeObjectURL(slot.preview.url as string);
        }
      });
    });
  }

  //// prueba merge
}
